import { z } from 'zod';

// A cleared form field arrives as '' rather than absent; treat it as omitted so
// optional fields don't fail their own format checks on an empty value.
const emptyToUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((v) => (v === '' ? undefined : v), schema.optional());

// ----- Roles -----
export const RoleName = z.enum(['Admin', 'Finance', 'HR', 'PM', 'Sales', 'Employee']);
export type RoleName = z.infer<typeof RoleName>;

// ----- Auth -----
export const EmployeeId = z
  .string()
  .trim()
  .min(1)
  .max(32)
  .transform((v) => v.toUpperCase());

export const LoginRequest = z.object({
  employeeId: EmployeeId,
  password: z.string().min(8).max(200),
  totp: z
    .string()
    .regex(/^\d{6}$/)
    .optional(),
});
export type LoginRequest = z.infer<typeof LoginRequest>;

export const ChangePasswordRequest = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(8).max(200),
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequest>;

export const ForgotPasswordRequest = z.object({
  employeeId: EmployeeId,
});
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequest>;

export const ResetPasswordRequest = z.object({
  token: z.string().min(1).max(200),
  newPassword: z.string().min(8).max(200),
});
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequest>;

export const LoginResponse = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ok'), personId: z.string(), needs2faSetup: z.boolean() }),
  z.object({ status: z.literal('needs_totp') }),
  z.object({ status: z.literal('invalid') }),
]);
export type LoginResponse = z.infer<typeof LoginResponse>;

export const TwoFactorSetupResponse = z.object({
  otpauthUri: z.string(),
  qrPngDataUrl: z.string(),
});

export const TwoFactorVerifyRequest = z.object({
  code: z.string().regex(/^\d{6}$/),
});

// ----- Person -----
export const PersonCreateRequest = z.object({
  name: z.string().min(1).max(160),
  email: z.string().email(),
  phone: z.string().max(32).optional(),
  dateJoined: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  roles: z.array(RoleName).min(1),
  initialPassword: z.string().min(8).max(200),
  // Blank means "allocate the next number"; anything else is used as given.
  employeeId: z
    .string()
    .trim()
    .max(32)
    .transform((v) => v.toUpperCase())
    .optional(),
});

export const NextEmployeeIdResponse = z.object({ employeeId: z.string() });
export type NextEmployeeIdResponse = z.infer<typeof NextEmployeeIdResponse>;
export type PersonCreateRequest = z.infer<typeof PersonCreateRequest>;

export const PersonSummary = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  employeeId: z.string().nullable(),
  status: z.enum(['active', 'exited']),
  roles: z.array(RoleName),
});
export type PersonSummary = z.infer<typeof PersonSummary>;

export const PersonSimple = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
});
export type PersonSimple = z.infer<typeof PersonSimple>;

// ----- Notifications -----
export const NotificationSummary = z.object({
  id: z.string(),
  kind: z.string(),
  subject: z.string(),
  body: z.string(),
  deepLink: z.string(),
  createdAt: z.string(),
  read: z.boolean(),
});
export type NotificationSummary = z.infer<typeof NotificationSummary>;

// ----- Session /me -----
export const MeResponse = z.object({
  person: PersonSummary,
  requires2fa: z.boolean(),
  twoFactorVerified: z.boolean(),
  mustChangePassword: z.boolean(),
});
export type MeResponse = z.infer<typeof MeResponse>;

// ----- Self-service profile -----
// Identity fields (email, employee ID, joining date, roles) stay admin-owned;
// a person edits only how they present themselves and how to reach them.
export const MyProfile = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  employeeId: z.string().nullable(),
  phone: z.string().nullable(),
  designation: z.string().nullable(),
  about: z.string().nullable(),
  dateJoined: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  roles: z.array(RoleName),
  csuiteRole: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  twoFactorVerified: z.boolean(),
});
export type MyProfile = z.infer<typeof MyProfile>;

export const MyProfileUpdateRequest = z.object({
  name: z.string().trim().min(1).max(160).optional(),
  phone: z.string().trim().max(32).nullable().optional(),
  designation: z.string().trim().max(120).nullable().optional(),
  about: z.string().trim().max(500).nullable().optional(),
});
export type MyProfileUpdateRequest = z.infer<typeof MyProfileUpdateRequest>;

// Who has a photo, and a version to cache-bust with; clients fetch the bytes
// from /people/:id/avatar?v=<version> only for people listed here.
export const PersonAvatarsResponse = z.array(
  z.object({ personId: z.string(), version: z.string() }),
);
export type PersonAvatarsResponse = z.infer<typeof PersonAvatarsResponse>;

// ----- Domain events (outbox payload envelopes) -----
export const DomainEventEnvelope = z.object({
  aggregate: z.string(),
  aggregateId: z.string(),
  eventType: z.string(),
  payload: z.record(z.unknown()),
});
export type DomainEventEnvelope = z.infer<typeof DomainEventEnvelope>;

// ----- Common error shape -----
export const ApiError = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
});
export type ApiError = z.infer<typeof ApiError>;

// ============================================================
// M1 — Company Vault, Company Profile, CRM
// ============================================================

// ----- Company Profile -----
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const BankDetails = z.object({
  bankName: z.string().min(1).max(160),
  accountName: z.string().min(1).max(160),
  accountNumber: z.string().min(1).max(64),
  ifsc: z.string().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/),
  branch: z.string().max(160).optional(),
});
export type BankDetails = z.infer<typeof BankDetails>;

export const CompanyProfileCreateRequest = z.object({
  legalName: z.string().min(1).max(240),
  gstin: z.string().length(15).optional(),
  pan: z
    .string()
    .regex(/^[A-Z]{5}\d{4}[A-Z]$/)
    .optional(),
  udyamNo: z.string().max(32).optional(),
  cin: z.string().max(32).optional(),
  registeredAddress: z.string().min(1),
  invoiceBankDetails: BankDetails,
  logoAttachmentId: z.string().optional(),
  validFrom: IsoDate,
});
export type CompanyProfileCreateRequest = z.infer<typeof CompanyProfileCreateRequest>;

export const CompanyProfileLogoRequest = z.object({
  logoAttachmentId: z.string().min(1),
});
export type CompanyProfileLogoRequest = z.infer<typeof CompanyProfileLogoRequest>;

export const CompanyProfileSummary = z.object({
  id: z.string(),
  legalName: z.string(),
  gstin: z.string().nullable(),
  pan: z.string().nullable(),
  udyamNo: z.string().nullable(),
  cin: z.string().nullable(),
  registeredAddress: z.string(),
  invoiceBankDetails: BankDetails,
  logoAttachmentId: z.string().nullable(),
  validFrom: z.string(),
});
export type CompanyProfileSummary = z.infer<typeof CompanyProfileSummary>;

// ----- Vault -----
export const VaultVisibility = z.enum(['admin_finance', 'all_staff']);
export type VaultVisibility = z.infer<typeof VaultVisibility>;

export const VaultDocTypeCreateRequest = z.object({
  name: z.string().min(1).max(120),
  defaultReminderDays: z.array(z.number().int().min(1).max(365)).min(1),
  defaultVisibility: VaultVisibility.default('admin_finance'),
});
export type VaultDocTypeCreateRequest = z.infer<typeof VaultDocTypeCreateRequest>;

export const VaultDocTypeSummary = z.object({
  id: z.string(),
  name: z.string(),
  defaultReminderDays: z.array(z.number().int()),
  defaultVisibility: VaultVisibility,
  active: z.boolean(),
});
export type VaultDocTypeSummary = z.infer<typeof VaultDocTypeSummary>;

export const VaultDocCreateMetadata = z.object({
  typeId: z.string().min(1),
  title: z.string().min(1).max(240),
  identifierNo: z.string().max(120).optional(),
  issueDate: IsoDate.optional(),
  expiryDate: IsoDate.optional(),
  visibility: VaultVisibility.optional(),
});
export type VaultDocCreateMetadata = z.infer<typeof VaultDocCreateMetadata>;

export const VaultDocSummary = z.object({
  id: z.string(),
  typeId: z.string(),
  typeName: z.string(),
  title: z.string(),
  identifierNo: z.string().nullable(),
  issueDate: z.string().nullable(),
  expiryDate: z.string().nullable(),
  attachmentId: z.string(),
  version: z.number().int(),
  supersedesId: z.string().nullable(),
  visibility: VaultVisibility,
  daysToExpiry: z.number().int().nullable(),
});
export type VaultDocSummary = z.infer<typeof VaultDocSummary>;

export const ShareBundleRequest = z.object({
  docIds: z.array(z.string()).min(1).max(50),
});
export type ShareBundleRequest = z.infer<typeof ShareBundleRequest>;

// ----- Lead economics / cash metrics / TCO -----
export const CacRow = z.object({
  channelId: z.string(),
  channelName: z.string(),
  spendPaise: z.number().int(),
  newClients: z.number().int(),
  cacPaise: z.number().int().nullable(),
});
export const CacResponse = z.object({
  from: z.string(),
  to: z.string(),
  rows: z.array(CacRow),
});
export type CacResponse = z.infer<typeof CacResponse>;

export const ClientLtvRow = z.object({
  clientId: z.string(),
  clientName: z.string(),
  channelName: z.string(),
  revenuePaise: z.number().int(),
  costPaise: z.number().int(),
  marginPaise: z.number().int(),
  cacSharePaise: z.number().int().nullable(),
  paybackMonths: z.number().int().nullable(),
});
export const LtvResponse = z.object({
  trailingMonths: z.number().int(),
  rows: z.array(ClientLtvRow),
});
export type LtvResponse = z.infer<typeof LtvResponse>;

export const ProjectCashRow = z.object({
  projectId: z.string(),
  code: z.string(),
  name: z.string(),
  inPaise: z.number().int(),
  outPaise: z.number().int(),
  npvPaise: z.number().int(),
  irrBpp: z.number().int().nullable(),
});
export const ProjectCashResponse = z.object({
  annualRateBpp: z.number().int(),
  rows: z.array(ProjectCashRow),
});
export type ProjectCashResponse = z.infer<typeof ProjectCashResponse>;

export const TcoRow = z.object({
  projectId: z.string(),
  code: z.string(),
  name: z.string(),
  payrollCostPaise: z.number().int(),
  expenseCostPaise: z.number().int(),
  overheadSharePaise: z.number().int(),
  tcoPaise: z.number().int(),
});
export const TcoResponse = z.object({
  overheadPaise: z.number().int(),
  rows: z.array(TcoRow),
});
export type TcoResponse = z.infer<typeof TcoResponse>;

// ----- CRM: Channels -----
export const LeadChannelUpsertRequest = z.object({
  name: z.string().min(1).max(120),
  active: z.boolean().default(true),
});
export type LeadChannelUpsertRequest = z.infer<typeof LeadChannelUpsertRequest>;

export const LeadChannelUpdateRequest = z
  .object({
    name: z.string().min(1).max(120).optional(),
    active: z.boolean().optional(),
  })
  .refine((v) => v.name !== undefined || v.active !== undefined, {
    message: 'Provide at least one field to update.',
  });
export type LeadChannelUpdateRequest = z.infer<typeof LeadChannelUpdateRequest>;

export const LeadChannelSummary = z.object({
  id: z.string(),
  name: z.string(),
  active: z.boolean(),
});
export type LeadChannelSummary = z.infer<typeof LeadChannelSummary>;

export const ChannelSpendUpsertRequest = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  amountPaise: z.number().int().min(0),
});
export type ChannelSpendUpsertRequest = z.infer<typeof ChannelSpendUpsertRequest>;

export const ChannelSpendGridCell = z.object({
  channelId: z.string(),
  month: z.string(),
  amountPaise: z.number().int(),
});
export type ChannelSpendGridCell = z.infer<typeof ChannelSpendGridCell>;

// ----- CRM: Clients -----
export const GstTreatment = z.enum(['registered', 'unregistered', 'composition', 'exempt']);
export type GstTreatment = z.infer<typeof GstTreatment>;

export const ClientStatus = z.enum(['active', 'archived', 'merged']);
export type ClientStatus = z.infer<typeof ClientStatus>;

export const ClientCreateRequest = z
  .object({
    legalName: z.string().min(1).max(240),
    displayName: emptyToUndefined(z.string().max(240)),
    gstin: emptyToUndefined(z.string().length(15)),
    gstTreatment: GstTreatment,
    pan: emptyToUndefined(z.string().regex(/^[A-Z]{5}\d{4}[A-Z]$/)),
    billingAddress: z.string().default(''),
    placeOfSupplyState: z.string().trim().min(1, 'Place of supply is required.').max(64),
    paymentTermsDays: z.number().int().min(0).max(365).default(30),
    leadChannelId: z.string().min(1),
    primaryContact: z
      .object({
        name: emptyToUndefined(z.string().max(160)),
        email: emptyToUndefined(z.string().email()),
        phone: emptyToUndefined(z.string().max(32)),
        title: emptyToUndefined(z.string().max(120)),
      })
      .optional()
      .refine((c) => !c || !!c.name || !(c.email || c.phone || c.title), {
        message: 'Contact name is required when any other contact detail is filled.',
        path: ['name'],
      }),
    force: z.boolean().default(false),
  })
  .refine((v) => v.gstTreatment !== 'registered' || (v.gstin && v.gstin.length === 15), {
    message: 'GSTIN is required for a registered client.',
    path: ['gstin'],
  });
export type ClientCreateRequest = z.infer<typeof ClientCreateRequest>;

export const DedupeSuggestion = z.object({
  id: z.string(),
  legalName: z.string(),
  gstin: z.string().nullable(),
  reason: z.enum(['gstin_exact', 'name_similar']),
  score: z.number(),
});
export type DedupeSuggestion = z.infer<typeof DedupeSuggestion>;

export const ClientCreateResponse = z.discriminatedUnion('status', [
  z.object({ status: z.literal('created'), clientId: z.string() }),
  z.object({
    status: z.literal('dedupe_suggestions'),
    suggestions: z.array(DedupeSuggestion),
  }),
]);
export type ClientCreateResponse = z.infer<typeof ClientCreateResponse>;

export const ClientSummary = z.object({
  id: z.string(),
  legalName: z.string(),
  displayName: z.string(),
  gstin: z.string().nullable(),
  gstTreatment: GstTreatment,
  status: ClientStatus,
  leadChannelId: z.string(),
  paymentTermsDays: z.number().int(),
  mergedIntoId: z.string().nullable(),
});
export type ClientSummary = z.infer<typeof ClientSummary>;

export const ClientSimple = z.object({
  id: z.string(),
  displayName: z.string(),
});
export type ClientSimple = z.infer<typeof ClientSimple>;
export const ClientsSimpleResponse = z.array(ClientSimple);

export const ContactCreateRequest = z.object({
  name: z.string().min(1).max(160),
  email: z.string().email().optional(),
  phone: z.string().max(32).optional(),
  title: z.string().max(120).optional(),
});
export type ContactCreateRequest = z.infer<typeof ContactCreateRequest>;

export const ContactSummary = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  title: z.string().nullable(),
});
export type ContactSummary = z.infer<typeof ContactSummary>;

export const ClientDetail = ClientSummary.extend({
  pan: z.string().nullable(),
  billingAddress: z.string(),
  placeOfSupplyState: z.string(),
  contacts: z.array(ContactSummary),
});
export type ClientDetail = z.infer<typeof ClientDetail>;

export const ClientMergeRequest = z.object({
  sourceId: z.string().min(1),
  targetId: z.string().min(1),
  reason: z.string().min(1).max(500),
});
export type ClientMergeRequest = z.infer<typeof ClientMergeRequest>;

export const ClientTimelineEntry = z.object({
  at: z.string(),
  kind: z.enum(['client', 'document', 'project']),
  title: z.string(),
  detail: z.string().nullable(),
  href: z.string().nullable(),
});
export type ClientTimelineEntry = z.infer<typeof ClientTimelineEntry>;

// Lead channel is frozen at creation (spec §7.1); changing it is Admin-only
// and needs a reason, which is why it travels separately from the other fields.
export const ClientUpdateRequest = z
  .object({
    legalName: z.string().min(1).max(240).optional(),
    displayName: z.string().min(1).max(240).optional(),
    gstin: z.string().length(15).nullable().optional(),
    gstTreatment: GstTreatment.optional(),
    pan: z
      .string()
      .regex(/^[A-Z]{5}\d{4}[A-Z]$/)
      .nullable()
      .optional(),
    billingAddress: z.string().optional(),
    placeOfSupplyState: z.string().trim().min(1, 'Place of supply is required.').max(64).optional(),
    paymentTermsDays: z.number().int().min(0).max(365).optional(),
    leadChannelId: z.string().min(1).optional(),
    leadChannelChangeReason: z.string().min(1).max(500).optional(),
  })
  .refine((v) => !v.leadChannelId || !!v.leadChannelChangeReason, {
    message: 'A reason is required when changing the lead channel.',
    path: ['leadChannelChangeReason'],
  });
export type ClientUpdateRequest = z.infer<typeof ClientUpdateRequest>;

// ----- CRM: GSTIN lookup (taxpayer details from the GST network) -----
export const GstinLookupStatus = z.object({ available: z.boolean() });
export type GstinLookupStatus = z.infer<typeof GstinLookupStatus>;

export const GstinLookupResult = z.object({
  gstin: z.string().length(15),
  legalName: z.string(),
  tradeName: z.string().nullable(),
  pan: z.string(),
  stateCode: z.string().length(2),
  registrationStatus: z.string(),
  taxpayerType: z.string().nullable(),
  constitution: z.string().nullable(),
  registrationDate: z.string().nullable(),
  address: z.string(),
  suggestedGstTreatment: GstTreatment,
});
export type GstinLookupResult = z.infer<typeof GstinLookupResult>;

// ============================================================
// M2 — Projects & Work
// ============================================================

const IsoDate2 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const YearMonth2 = z.string().regex(/^\d{4}-\d{2}$/);

// ----- Cost centers -----
export const CostCenterCreateRequest = z.object({
  name: z.string().min(1).max(120),
  isDefaultOverhead: z.boolean().default(false),
});
export type CostCenterCreateRequest = z.infer<typeof CostCenterCreateRequest>;

export const CostCenterRenameRequest = z.object({
  name: z.string().trim().min(1).max(120),
});
export type CostCenterRenameRequest = z.infer<typeof CostCenterRenameRequest>;

export const CostCenterSummary = z.object({
  id: z.string(),
  name: z.string(),
  isDefaultOverhead: z.boolean(),
  active: z.boolean(),
});
export type CostCenterSummary = z.infer<typeof CostCenterSummary>;

// ----- Projects -----
export const ProjectType = z.enum(['client_delivery', 'internal_initiative']);
export type ProjectType = z.infer<typeof ProjectType>;

export const ProjectStatus = z.enum(['active', 'on_hold', 'closed']);
export type ProjectStatus = z.infer<typeof ProjectStatus>;

export const RenewalKind = z.enum(['domain', 'hosting', 'amc', 'ssl', 'other']);
export type RenewalKind = z.infer<typeof RenewalKind>;

const IsoDateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const RenewalInput = z.object({
  projectId: z.string().nullable().optional(),
  clientId: z.string().nullable().optional(),
  kind: RenewalKind,
  label: z.string().trim().min(1).max(160),
  provider: z.string().trim().max(160).nullable().optional(),
  expiresOn: IsoDateString.nullable().optional(),
  amountPaise: z.number().int().nonnegative().nullable().optional(),
  cycleMonths: z.number().int().min(1).max(120).nullable().optional(),
  remindDays: z.array(z.number().int().min(0).max(730)).max(8).optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export type RenewalInput = z.infer<typeof RenewalInput>;

export const RenewalUpdateRequest = RenewalInput.partial().extend({
  archived: z.boolean().optional(),
});
export type RenewalUpdateRequest = z.infer<typeof RenewalUpdateRequest>;

export const RenewalProjectRef = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  status: ProjectStatus,
});
export type RenewalProjectRef = z.infer<typeof RenewalProjectRef>;

export const RenewalSummary = z.object({
  id: z.string(),
  projectId: z.string().nullable(),
  project: RenewalProjectRef.nullable(),
  clientId: z.string().nullable(),
  client: z.object({ id: z.string(), displayName: z.string() }).nullable(),
  kind: RenewalKind,
  label: z.string(),
  provider: z.string().nullable(),
  expiresOn: IsoDateString.nullable(),
  daysLeft: z.number().int().nullable(),
  amountPaise: z.number().int().nullable(),
  cycleMonths: z.number().int().nullable(),
  remindDays: z.array(z.number().int()),
  notes: z.string().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type RenewalSummary = z.infer<typeof RenewalSummary>;
export const RenewalListResponse = z.array(RenewalSummary);

export const RenewalStatusFilter = z.enum(['active', 'archived', 'all']);
export type RenewalStatusFilter = z.infer<typeof RenewalStatusFilter>;

export const ProjectCreateRequest = z.object({
  code: z
    .string()
    .min(1)
    .max(32)
    .regex(/^[A-Z0-9._-]+$/i, 'code must be alphanumeric + . _ -')
    .optional(),
  name: z.string().min(1).max(240),
  type: ProjectType,
  clientId: z.string().optional(),
  costCenterId: z.string().optional(),
  actualHoursOn: z.boolean().default(false),
  renewals: z
    .array(RenewalInput.omit({ projectId: true }))
    .max(10)
    .optional(),
});
export type ProjectCreateRequest = z.infer<typeof ProjectCreateRequest>;

export const ProjectUpdateRequest = z.object({
  name: z.string().min(1).max(240).optional(),
  clientId: z.string().min(1).optional(),
  costCenterId: z.string().min(1).optional(),
  managerId: z.string().min(1).nullable().optional(),
});
export type ProjectUpdateRequest = z.infer<typeof ProjectUpdateRequest>;

export const ProjectStatusChangeRequest = z.object({
  to: ProjectStatus,
  reason: z.string().max(500).optional(),
});
export type ProjectStatusChangeRequest = z.infer<typeof ProjectStatusChangeRequest>;

export const ProjectSummary = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  type: ProjectType,
  status: ProjectStatus,
  clientId: z.string().nullable(),
  costCenterId: z.string(),
  actualHoursOn: z.boolean(),
  managerId: z.string().nullable(),
  createdAt: z.string(),
  nextRenewal: z
    .object({
      kind: RenewalKind,
      label: z.string(),
      expiresOn: IsoDateString,
      daysLeft: z.number().int(),
    })
    .nullable(),
});
export type ProjectSummary = z.infer<typeof ProjectSummary>;

export const BudgetKind = z.enum(['fee', 'cost']);
export type BudgetKind = z.infer<typeof BudgetKind>;

export const BudgetRevisionSummary = z.object({
  id: z.string(),
  kind: BudgetKind,
  amountPaise: z.number().int(),
  validFrom: z.string(),
  approvedBy: z.string(),
  reason: z.string(),
});
export type BudgetRevisionSummary = z.infer<typeof BudgetRevisionSummary>;

export const BudgetRevisionCreateRequest = z.object({
  kind: BudgetKind,
  amountPaise: z.number().int().min(0),
  validFrom: IsoDate2,
  reason: z.string().min(1).max(500),
});
export type BudgetRevisionCreateRequest = z.infer<typeof BudgetRevisionCreateRequest>;

export const ProjectDetail = ProjectSummary.extend({
  reviewDueOn: z.string().nullable(),
  ideaId: z.string().nullable(),
  currentFeePaise: z.number().int(),
  currentCostPaise: z.number().int(),
  openTaskCount: z.number().int(),
  budgetRevisions: z.array(BudgetRevisionSummary),
  renewals: z.array(RenewalSummary),
});
export type ProjectDetail = z.infer<typeof ProjectDetail>;

// ----- Tasks -----
export const TaskStatusKind = z.enum(['open', 'finished']);
export type TaskStatusKind = z.infer<typeof TaskStatusKind>;

export const TaskStatusColor = z.enum([
  'slate',
  'blue',
  'emerald',
  'amber',
  'rose',
  'violet',
  'cyan',
  'orange',
]);
export type TaskStatusColor = z.infer<typeof TaskStatusColor>;

export const TaskStatusDef = z.object({
  id: z.string(),
  key: z.string(),
  label: z.string(),
  kind: TaskStatusKind,
  color: TaskStatusColor,
  position: z.number().int(),
  isDefault: z.boolean(),
  archived: z.boolean(),
  taskCount: z.number().int(),
});
export type TaskStatusDef = z.infer<typeof TaskStatusDef>;

export const TaskStatusCreateRequest = z.object({
  label: z.string().min(1).max(64),
  kind: TaskStatusKind,
  color: TaskStatusColor,
});
export type TaskStatusCreateRequest = z.infer<typeof TaskStatusCreateRequest>;

export const TaskStatusUpdateRequest = z.object({
  label: z.string().min(1).max(64).optional(),
  kind: TaskStatusKind.optional(),
  color: TaskStatusColor.optional(),
  archived: z.boolean().optional(),
});
export type TaskStatusUpdateRequest = z.infer<typeof TaskStatusUpdateRequest>;

export const TaskStatusReorderRequest = z.object({
  orderedIds: z.array(z.string().min(1)).min(1),
});
export type TaskStatusReorderRequest = z.infer<typeof TaskStatusReorderRequest>;

export const TaskCreateRequest = z.object({
  title: z.string().min(1).max(240),
  description: z.string().max(4000).optional(),
  assigneeId: z.string().min(1),
  dueOn: IsoDate2.optional(),
  parentTaskId: z.string().optional(),
  statusId: z.string().min(1).optional(),
});
export type TaskCreateRequest = z.infer<typeof TaskCreateRequest>;

export const TaskUpdateRequest = z.object({
  title: z.string().min(1).max(240).optional(),
  description: z.string().max(4000).nullable().optional(),
  assigneeId: z.string().min(1).optional(),
  dueOn: IsoDate2.nullable().optional(),
  statusId: z.string().min(1).optional(),
  actualHours: z.number().int().min(0).nullable().optional(),
});
export type TaskUpdateRequest = z.infer<typeof TaskUpdateRequest>;

export const TaskSummary = z.object({
  id: z.string(),
  projectId: z.string(),
  parentTaskId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  assigneeId: z.string(),
  assigneeName: z.string().nullable(),
  dueOn: z.string().nullable(),
  statusId: z.string(),
  statusKey: z.string(),
  statusLabel: z.string(),
  statusKind: TaskStatusKind,
  actualHours: z.number().int().nullable(),
  commentCount: z.number().int(),
});
export type TaskSummary = z.infer<typeof TaskSummary>;

export const TaskTreeNode: z.ZodType<TaskTreeNodeType> = z.lazy(() =>
  TaskSummary.extend({ children: z.array(TaskTreeNode) }),
);
export type TaskTreeNodeType = z.infer<typeof TaskSummary> & { children: TaskTreeNodeType[] };

export const TaskHistoryEntry = z.object({
  id: z.string(),
  action: z.string(),
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  diff: z.record(z.unknown()).nullable(),
  reason: z.string().nullable(),
  createdAt: z.string(),
});
export type TaskHistoryEntry = z.infer<typeof TaskHistoryEntry>;

export const TaskCommentCreateRequest = z.object({
  body: z.string().trim().min(1).max(5_000),
});
export type TaskCommentCreateRequest = z.infer<typeof TaskCommentCreateRequest>;

export const TaskComment = z.object({
  id: z.string(),
  taskId: z.string(),
  authorId: z.string(),
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: z.string(),
});
export type TaskComment = z.infer<typeof TaskComment>;

// ----- My Tasks -----
export const MyTasksInbox = z.object({
  today: z.array(TaskSummary),
  overdue: z.array(TaskSummary),
  upcoming: z.array(TaskSummary),
  // Due beyond the upcoming window, or with no due date — still open work the
  // assignee must be able to see and clock time against.
  later: z.array(TaskSummary),
  // Finished work stays visible to its assignee; reopening goes through the board.
  completed: z.array(TaskSummary),
});
export type MyTasksInbox = z.infer<typeof MyTasksInbox>;

// ----- Allocations -----
export const AllocationRow = z.object({
  personId: z.string(),
  projectId: z.string(),
  percent: z.number().int().min(0).max(100),
});
export type AllocationRow = z.infer<typeof AllocationRow>;

export const AllocationUpsertRequest = z.object({
  month: YearMonth2,
  rows: z.array(AllocationRow),
});
export type AllocationUpsertRequest = z.infer<typeof AllocationUpsertRequest>;

export const AllocationCopyForwardRequest = z.object({
  fromMonth: YearMonth2,
});
export type AllocationCopyForwardRequest = z.infer<typeof AllocationCopyForwardRequest>;

export const AllocationCell = AllocationRow.extend({
  month: YearMonth2,
  locked: z.boolean(),
});
export type AllocationCell = z.infer<typeof AllocationCell>;

export const AllocationMatrixResponse = z.object({
  month: YearMonth2,
  cells: z.array(AllocationCell),
});
export type AllocationMatrixResponse = z.infer<typeof AllocationMatrixResponse>;

// ============================================================
// M3 — Ledger core
// ============================================================

const IsoDate3 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

// ----- Accounts -----
export const AccountKind = z.enum(['bank', 'cash']);
export type AccountKind = z.infer<typeof AccountKind>;

export const AccountCreateRequest = z.object({
  kind: AccountKind,
  name: z.string().min(1).max(120),
  numberPlaintext: z.string().min(4).max(64), // server masks + stores
  openingBalancePaise: z.number().int().default(0),
  openingAsOf: IsoDate3,
});
export type AccountCreateRequest = z.infer<typeof AccountCreateRequest>;

export const AccountSummary = z.object({
  id: z.string(),
  kind: AccountKind,
  name: z.string(),
  numberMasked: z.string(),
  openingBalancePaise: z.number().int(),
  openingAsOf: z.string(),
  active: z.boolean(),
  currentBalancePaise: z.number().int(), // opening + Σ signed(txns)
});
export type AccountSummary = z.infer<typeof AccountSummary>;

// ----- Categories -----
export const CategoryKind = z.enum(['income', 'expense', 'transfer', 'advance']);
export type CategoryKind = z.infer<typeof CategoryKind>;

export const CategoryCreateRequest = z.object({
  name: z.string().min(1).max(120),
  kind: CategoryKind,
});
export type CategoryCreateRequest = z.infer<typeof CategoryCreateRequest>;

export const CategorySummary = z.object({
  id: z.string(),
  name: z.string(),
  kind: CategoryKind,
  active: z.boolean(),
});
export type CategorySummary = z.infer<typeof CategorySummary>;

// ----- Transactions -----
export const Direction = z.enum(['in', 'out']);
export type Direction = z.infer<typeof Direction>;

export const TransactionStatus = z.enum(['recorded', 'mapped', 'reconciled', 'reversed']);
export type TransactionStatus = z.infer<typeof TransactionStatus>;

export const GstReasonCode = z.enum([
  'exempt',
  'nil_rated',
  'unregistered_counterparty',
  'composition',
  'non_supply_advance',
  'non_supply_reimbursement',
  'non_supply_capital',
]);
export type GstReasonCode = z.infer<typeof GstReasonCode>;

export const TransactionCreateRequest = z.object({
  accountId: z.string().min(1),
  businessDate: IsoDate3,
  amountPaise: z.number().int().positive(),
  direction: Direction,
  categoryId: z.string().optional(),
  description: z.string().min(1).max(500),
  gstRelevant: z.boolean().default(false),
  gstReasonCode: GstReasonCode.optional(),
  projectId: z.string().optional(),
  costCenterId: z.string().optional(),
  clientId: z.string().optional(),
});
export type TransactionCreateRequest = z.infer<typeof TransactionCreateRequest>;

export const PastedRowsPreviewRequest = z.object({
  text: z.string().max(200_000),
});
export type PastedRowsPreviewRequest = z.infer<typeof PastedRowsPreviewRequest>;

export const PastedRowPreview = z.object({
  line: z.number().int(),
  businessDate: IsoDate3,
  amountPaise: z.number().int(),
  direction: Direction,
  description: z.string(),
  categoryHint: z.string().optional(),
});
export type PastedRowPreview = z.infer<typeof PastedRowPreview>;

export const PastedRowError = z.object({
  line: z.number().int(),
  message: z.string(),
});
export type PastedRowError = z.infer<typeof PastedRowError>;

export const PastedRowsPreviewResponse = z.object({
  ok: z.array(PastedRowPreview),
  errors: z.array(PastedRowError),
});
export type PastedRowsPreviewResponse = z.infer<typeof PastedRowsPreviewResponse>;

export const TransactionBatchCreateRequest = z.object({
  accountId: z.string().min(1),
  defaultCategoryId: z.string().optional(),
  text: z.string().max(200_000), // server re-parses (trust boundary)
});
export type TransactionBatchCreateRequest = z.infer<typeof TransactionBatchCreateRequest>;

export const TransferCreateRequest = z.object({
  fromAccountId: z.string().min(1),
  toAccountId: z.string().min(1),
  businessDate: IsoDate3,
  amountPaise: z.number().int().positive(),
  description: z.string().min(1).max(500),
  projectId: z.string().optional(),
  costCenterId: z.string().optional(),
});
export type TransferCreateRequest = z.infer<typeof TransferCreateRequest>;

export const TransactionMapRequest = z.object({
  projectId: z.string().optional(),
  costCenterId: z.string().optional(),
});
export type TransactionMapRequest = z.infer<typeof TransactionMapRequest>;

export const TransactionReverseRequest = z.object({
  reason: z.string().min(1).max(500),
});
export type TransactionReverseRequest = z.infer<typeof TransactionReverseRequest>;

export const TransactionSummary = z.object({
  id: z.string(),
  accountId: z.string(),
  businessDate: z.string(),
  amountPaise: z.number().int(),
  direction: Direction,
  categoryId: z.string().nullable(),
  categoryName: z.string().nullable(),
  description: z.string(),
  projectId: z.string().nullable(),
  costCenterId: z.string().nullable(),
  status: TransactionStatus,
  transferGroupId: z.string().nullable(),
  reversalOfId: z.string().nullable(),
});
export type TransactionSummary = z.infer<typeof TransactionSummary>;

export const TransactionListResponse = z.object({
  items: z.array(TransactionSummary),
  total: z.number().int(),
  runningBalancePaise: z.number().int().optional(), // only when filtered by accountId
});
export type TransactionListResponse = z.infer<typeof TransactionListResponse>;

export const TransactionDetail = TransactionSummary.extend({
  gstRelevant: z.boolean(),
  gstReasonCode: GstReasonCode.nullable(),
  clientId: z.string().nullable(),
  documentId: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  source: z.string(),
});
export type TransactionDetail = z.infer<typeof TransactionDetail>;

// ============================================================
// M4 — Statement Ingestion
// ============================================================

const IsoDate4 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

// ----- Bank templates -----
export const BankTemplateFileFormat = z.enum(['csv', 'xlsx', 'pdf']);
export type BankTemplateFileFormat = z.infer<typeof BankTemplateFileFormat>;

// The config schema is intentionally loose — see M4_PLAN §"BankTemplate.config schema".
// The parser reads the fields it knows about and ignores extras. For fileFormat=pdf,
// see the `pdf` sub-config; `columns` may be `{}`.
export const BankTemplateConfig = z.object({
  delimiter: z.string().optional(),
  sheet: z.union([z.string(), z.number().int()]).optional(),
  headerRowIndex: z.number().int().min(-1).default(0),
  columns: z.record(
    z.object({
      at: z.union([z.string(), z.number().int()]),
      optional: z.boolean().optional(),
      map: z.record(z.string()).optional(),
    }),
  ),
  amountPolicy: z.enum(['signed', 'single_column', 'withdrawal_deposit_columns']),
  withdrawalColumn: z.union([z.string(), z.number().int()]).optional(),
  depositColumn: z.union([z.string(), z.number().int()]).optional(),
  dateFormat: z.enum(['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', 'DD MMM YYYY', 'DD/MM/YY']),
  openingBalanceRow: z
    .object({
      strategy: z.enum(['match_description', 'first_row_field', 'running_balance', 'none']),
      descriptionRegex: z.string().optional(),
      amountColumn: z.union([z.string(), z.number().int()]).optional(),
    })
    .optional(),
  closingBalanceRow: z
    .object({
      strategy: z.enum(['match_description', 'last_row_field', 'running_balance', 'none']),
      descriptionRegex: z.string().optional(),
      amountColumn: z.union([z.string(), z.number().int()]).optional(),
    })
    .optional(),
  /**
   * PDF-specific parser config. Only consulted when fileFormat === 'pdf'.
   * rowPattern uses named groups: (?<date>…) (?<description>…) and either
   * (?<amount>…) (+ optional (?<direction>…)) OR (?<withdrawal>…) (?<deposit>…).
   * Balance patterns extract from the flattened per-page text.
   */
  pdf: z
    .object({
      pageStart: z.number().int().min(1).optional(),
      pageEnd: z.number().int().min(1).optional(),
      skipLinePatterns: z.array(z.string()).default([]),
      rowPattern: z.string(),
      directionMap: z.record(z.string()).optional(),
      openingBalancePattern: z.string().optional(),
      closingBalancePattern: z.string().optional(),
      continuationJoin: z.boolean().default(false),
    })
    .optional(),
});
export type BankTemplateConfig = z.infer<typeof BankTemplateConfig>;

export const BankTemplateCreateRequest = z.object({
  name: z.string().min(1).max(120),
  fileFormat: BankTemplateFileFormat,
  config: BankTemplateConfig,
});
export type BankTemplateCreateRequest = z.infer<typeof BankTemplateCreateRequest>;

export const BankTemplateSummary = z.object({
  id: z.string(),
  name: z.string(),
  fileFormat: BankTemplateFileFormat,
  active: z.boolean(),
  config: BankTemplateConfig,
});
export type BankTemplateSummary = z.infer<typeof BankTemplateSummary>;

// ----- Statement files -----
export const StatementFileStatus = z.enum(['uploaded', 'parsed', 'in_review', 'posted']);
export type StatementFileStatus = z.infer<typeof StatementFileStatus>;

export const StatementRowStatus = z.enum(['pending', 'suggested', 'confirmed', 'skipped']);
export type StatementRowStatus = z.infer<typeof StatementRowStatus>;

export const StatementUploadResult = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('created'),
    fileId: z.string(),
    rowCount: z.number().int(),
    openingBalancePaise: z.number().int().nullable(),
    closingBalancePaise: z.number().int().nullable(),
  }),
  z.object({
    status: z.literal('duplicate'),
    fileId: z.string(),
    existingStatus: StatementFileStatus,
  }),
]);
export type StatementUploadResult = z.infer<typeof StatementUploadResult>;

export const StatementSummary = z.object({
  id: z.string(),
  accountId: z.string(),
  bankTemplateId: z.string(),
  filename: z.string(),
  sha256: z.string(),
  fromDate: z.string().nullable(),
  toDate: z.string().nullable(),
  openingBalancePaise: z.number().int().nullable(),
  closingBalancePaise: z.number().int().nullable(),
  status: StatementFileStatus,
  postedAt: z.string().nullable(),
  postedBy: z.string().nullable(),
  balanceOverride: z.string().nullable(),
  rowCounts: z.object({
    pending: z.number().int(),
    suggested: z.number().int(),
    confirmed: z.number().int(),
    skipped: z.number().int(),
  }),
});
export type StatementSummary = z.infer<typeof StatementSummary>;

export const StatementRowSummary = z.object({
  id: z.string(),
  lineNo: z.number().int(),
  businessDate: z.string(),
  amountPaise: z.number().int(),
  direction: Direction,
  descriptor: z.string(),
  rawDescription: z.string(),
  status: StatementRowStatus,
  suggestedCategoryId: z.string().nullable(),
  suggestedCategoryName: z.string().nullable(),
  suggestedProjectId: z.string().nullable(),
  suggestedCostCenterId: z.string().nullable(),
  matchedTransactionId: z.string().nullable(),
});
export type StatementRowSummary = z.infer<typeof StatementRowSummary>;

export const StatementRowConfirmRequest = z.object({
  projectId: z.string().optional(),
  costCenterId: z.string().optional(),
  categoryId: z.string().optional(),
});
export type StatementRowConfirmRequest = z.infer<typeof StatementRowConfirmRequest>;

export const StatementRowSkipRequest = z.object({
  reason: z.string().min(1).max(500),
});
export type StatementRowSkipRequest = z.infer<typeof StatementRowSkipRequest>;

// Without `mapping`, every row must carry a precomputed suggestion. With it,
// the same mapping is applied to all of the rows and suggestions are ignored.
export const StatementBulkConfirmRequest = z.object({
  rowIds: z.array(z.string()).min(1).max(500),
  mapping: StatementRowConfirmRequest.optional(),
});
export type StatementBulkConfirmRequest = z.infer<typeof StatementBulkConfirmRequest>;

export const StatementBulkSkipRequest = z.object({
  rowIds: z.array(z.string()).min(1).max(500),
  reason: z.string().min(1).max(500),
});
export type StatementBulkSkipRequest = z.infer<typeof StatementBulkSkipRequest>;

export const StatementPostRequest = z.object({
  overrideReason: z.string().min(1).max(500).optional(),
});
export type StatementPostRequest = z.infer<typeof StatementPostRequest>;

export const StatementPostResult = z.object({
  fileId: z.string(),
  status: StatementFileStatus,
  balanceTie: z.object({
    matched: z.boolean(),
    deltaPaise: z.number().int(),
    overridden: z.boolean(),
  }),
});
export type StatementPostResult = z.infer<typeof StatementPostResult>;

// ----- Mapping rules -----
export const MappingRuleSummary = z.object({
  id: z.string(),
  pattern: z.string(),
  accountId: z.string().nullable(),
  suggestCategoryId: z.string().nullable(),
  suggestProjectId: z.string().nullable(),
  suggestCostCenterId: z.string().nullable(),
  active: z.boolean(),
  learnedFromConfirmation: z.boolean(),
  hitCount: z.number().int(),
});
export type MappingRuleSummary = z.infer<typeof MappingRuleSummary>;

// ============================================================
// M5 — Billing
// ============================================================

const IsoDate5 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const DocType = z.enum(['tax_invoice', 'bill_of_supply', 'credit_note', 'receipt']);
export type DocType = z.infer<typeof DocType>;

export const DocumentStatus = z.enum(['draft', 'issued', 'partially_paid', 'paid', 'written_off']);
export type DocumentStatus = z.infer<typeof DocumentStatus>;

export const DocumentLineInput = z.object({
  description: z.string().min(1).max(500),
  hsnSac: z.string().max(32).optional(),
  qty: z.number().int().min(1), // milli-units (1000 = 1.000)
  ratePaise: z.number().int().min(0),
  gstRateBpp: z.number().int().min(0).max(10_000).default(0), // basis-points-of-percent
});
export type DocumentLineInput = z.infer<typeof DocumentLineInput>;

export const DocumentCreateRequest = z.object({
  docType: DocType,
  clientId: z.string().min(1),
  projectId: z.string().min(1).optional(),
  issueDate: IsoDate5,
  dueDate: IsoDate5.optional(),
  placeOfSupplyState: z.string().min(1).max(64),
  number: z.string().trim().min(1).max(48).optional(),
  gstReasonCode: GstReasonCode.optional(),
  customerNotes: z.string().max(2000).optional(),
  termsText: z.string().max(4000).optional(),
  lines: z.array(DocumentLineInput).min(1).max(200),
});
export type DocumentCreateRequest = z.infer<typeof DocumentCreateRequest>;

export const DocumentUpdateDraftRequest = DocumentCreateRequest;
export type DocumentUpdateDraftRequest = z.infer<typeof DocumentUpdateDraftRequest>;

export const CreditNoteCreateRequest = z.object({
  refDocumentId: z.string().min(1),
  issueDate: IsoDate5,
  lines: z.array(DocumentLineInput).min(1).max(200),
  reason: z.string().min(1).max(500),
});
export type CreditNoteCreateRequest = z.infer<typeof CreditNoteCreateRequest>;

export const PaymentApplicationRequest = z.object({
  transactionId: z.string().min(1),
  amountPaise: z.number().int().positive(),
});
export type PaymentApplicationRequest = z.infer<typeof PaymentApplicationRequest>;

export const WriteOffRequest = z.object({
  reason: z.string().min(1).max(500),
});
export type WriteOffRequest = z.infer<typeof WriteOffRequest>;

// ----- Identity snapshot (frozen at issue-time; drives PDF) -----
export const IdentitySnapshot = z.object({
  legalName: z.string(),
  gstin: z.string().nullable(),
  pan: z.string().nullable(),
  udyamNo: z.string().nullable(),
  cin: z.string().nullable(),
  registeredAddress: z.string(),
  invoiceBankDetails: BankDetails,
  logoAttachmentId: z.string().nullable(),
});
export type IdentitySnapshot = z.infer<typeof IdentitySnapshot>;

// ----- Summaries / details -----
export const DocumentLineSummary = z.object({
  id: z.string(),
  lineNo: z.number().int(),
  description: z.string(),
  hsnSac: z.string().nullable(),
  qty: z.number().int(),
  ratePaise: z.number().int(),
  taxablePaise: z.number().int(),
  cgstPct: z.number().int(),
  cgstPaise: z.number().int(),
  sgstPct: z.number().int(),
  sgstPaise: z.number().int(),
  igstPct: z.number().int(),
  igstPaise: z.number().int(),
});
export type DocumentLineSummary = z.infer<typeof DocumentLineSummary>;

export const PaymentApplicationSummary = z.object({
  id: z.string(),
  transactionId: z.string(),
  amountPaise: z.number().int(),
  createdAt: z.string(),
});
export type PaymentApplicationSummary = z.infer<typeof PaymentApplicationSummary>;

export const DocumentSummary = z.object({
  id: z.string(),
  docType: DocType,
  number: z.string().nullable(), // null for drafts
  requestedNumber: z.string().nullable(), // a draft's chosen number, used when it is issued
  fy: z.number().int(),
  clientId: z.string(),
  projectId: z.string().nullable(),
  issueDate: z.string(),
  dueDate: z.string().nullable(),
  placeOfSupplyState: z.string(),
  status: DocumentStatus,
  subtotalPaise: z.number().int(),
  taxPaise: z.number().int(),
  totalPaise: z.number().int(),
  amountDuePaise: z.number().int(),
  refDocumentId: z.string().nullable(),
});
export type DocumentSummary = z.infer<typeof DocumentSummary>;

export const DocumentDetail = DocumentSummary.extend({
  lines: z.array(DocumentLineSummary),
  payments: z.array(PaymentApplicationSummary),
  gstReasonCode: GstReasonCode.nullable(),
  pdfAttachmentId: z.string().nullable(),
  issuedAt: z.string().nullable(),
  identitySnapshot: IdentitySnapshot.nullable(),
  writeOffReason: z.string().nullable(),
  writtenOffAt: z.string().nullable(),
  customerNotes: z.string().nullable(),
  termsText: z.string().nullable(),
});
export type DocumentDetail = z.infer<typeof DocumentDetail>;

export const NextDocumentNumberResponse = z.object({
  expectedNumber: z.string(),
  fy: z.number().int(),
  template: z.string(),
  hasCompanyProfile: z.boolean(),
  companyStateCode: z.string().nullable(),
});
export type NextDocumentNumberResponse = z.infer<typeof NextDocumentNumberResponse>;

export const DocumentNumberFormatCreateRequest = z.object({
  docType: DocType,
  template: z.string().trim().min(1).max(48),
  startAt: z.number().int().min(1).max(99_999_999).optional(),
  validFrom: IsoDate,
});
export type DocumentNumberFormatCreateRequest = z.infer<typeof DocumentNumberFormatCreateRequest>;

export const DocumentNumberFormatView = z.object({
  id: z.string().nullable(),
  docType: DocType,
  template: z.string(),
  startAt: z.number().int().nullable(),
  validFrom: IsoDate.nullable(),
  example: z.string(),
  isCurrent: z.boolean(),
  createdBy: z.string().nullable(),
  createdAt: z.string().nullable(),
});
export type DocumentNumberFormatView = z.infer<typeof DocumentNumberFormatView>;

export const DocumentNumberFormatListResponse = z.array(DocumentNumberFormatView);

export const DocumentListResponse = z.object({
  items: z.array(DocumentSummary),
  total: z.number().int(),
});
export type DocumentListResponse = z.infer<typeof DocumentListResponse>;

// ----- AR aging -----
export const AgingBucketKey = z.enum(['current', '0_30', '31_60', '61_90', '90_plus']);
export type AgingBucketKey = z.infer<typeof AgingBucketKey>;

export const ArAgingClientRow = z.object({
  clientId: z.string(),
  clientName: z.string(),
  current: z.number().int(),
  b0_30: z.number().int(),
  b31_60: z.number().int(),
  b61_90: z.number().int(),
  b90_plus: z.number().int(),
  total: z.number().int(),
});
export type ArAgingClientRow = z.infer<typeof ArAgingClientRow>;

export const ArAgingResponse = z.object({
  asOf: IsoDate5,
  totals: z.object({
    current: z.number().int(),
    b0_30: z.number().int(),
    b31_60: z.number().int(),
    b61_90: z.number().int(),
    b90_plus: z.number().int(),
    total: z.number().int(),
  }),
  rows: z.array(ArAgingClientRow),
});
export type ArAgingResponse = z.infer<typeof ArAgingResponse>;

// ============================================================
// M6 — Period Close
// ============================================================

const Month6 = z.string().regex(/^\d{4}-\d{2}$/);

export const PeriodStatus = z.enum(['open', 'soft_closed', 'closed']);
export type PeriodStatus = z.infer<typeof PeriodStatus>;

export const GateStatus = z.enum(['pass', 'warn', 'fail', 'not_applicable']);
export type GateStatus = z.infer<typeof GateStatus>;

export const SnapshotKind = z.enum([
  'ar_aging',
  'project_pnl',
  'balances',
  'headcount',
  'unmapped',
]);
export type SnapshotKind = z.infer<typeof SnapshotKind>;

export const ChecklistItem = z.object({
  key: z.string(),
  label: z.string(),
  status: GateStatus,
  blocking: z.boolean(),
  details: z.record(z.unknown()).optional(),
});
export type ChecklistItem = z.infer<typeof ChecklistItem>;

export const ChecklistResponse = z.object({
  month: Month6,
  periodStatus: PeriodStatus,
  reopenCount: z.number().int().min(0),
  items: z.array(ChecklistItem),
  canSoftClose: z.boolean(),
  canClose: z.boolean(),
  canReopen: z.boolean(),
});
export type ChecklistResponse = z.infer<typeof ChecklistResponse>;

export const SoftCloseRequest = z.object({});
export type SoftCloseRequest = z.infer<typeof SoftCloseRequest>;

export const CloseRequest = z.object({});
export type CloseRequest = z.infer<typeof CloseRequest>;

export const ReopenRequest = z.object({
  reason: z.string().min(1).max(500),
});
export type ReopenRequest = z.infer<typeof ReopenRequest>;

export const PeriodSummary = z.object({
  month: Month6,
  status: PeriodStatus,
  reopenCount: z.number().int().min(0),
  closedAt: z.string().nullable(),
  closedBy: z.string().nullable(),
});
export type PeriodSummary = z.infer<typeof PeriodSummary>;

export const SnapshotSummary = z.object({
  id: z.string(),
  month: Month6,
  kind: SnapshotKind,
  restated: z.boolean(),
  createdAt: z.string(),
  payload: z.unknown(),
});
export type SnapshotSummary = z.infer<typeof SnapshotSummary>;

// ============================================================
// M7 — People / Attendance & Leave
// ============================================================

const IsoDate7 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const Month7 = z.string().regex(/^\d{4}-\d{2}$/);

export const LeaveStatus = z.enum(['requested', 'approved', 'rejected', 'availed', 'cancelled']);
export type LeaveStatus = z.infer<typeof LeaveStatus>;

// ----- Leave Type -----
export const LeaveQuotaPeriod = z.enum(['year', 'month']);
export type LeaveQuotaPeriod = z.infer<typeof LeaveQuotaPeriod>;

/** Days x10 that fit in one period: a year of 366 days or a month of 31. */
export const LEAVE_QUOTA_MAX_X10: Record<LeaveQuotaPeriod, number> = { year: 3660, month: 310 };

const quotaFitsPeriod = (v: { quotaX10?: number; quotaPeriod?: LeaveQuotaPeriod }): boolean =>
  v.quotaX10 === undefined ||
  v.quotaPeriod === undefined ||
  v.quotaX10 <= LEAVE_QUOTA_MAX_X10[v.quotaPeriod];

export const LeaveTypeCreateRequest = z
  .object({
    name: z.string().min(1).max(120),
    paid: z.boolean().default(true),
    quotaX10: z.number().int().min(0).max(3660).default(0),
    quotaPeriod: LeaveQuotaPeriod.default('year'),
  })
  .refine(quotaFitsPeriod, {
    message: 'quota exceeds the length of the period',
    path: ['quotaX10'],
  });
export type LeaveTypeCreateRequest = z.infer<typeof LeaveTypeCreateRequest>;

export const LeaveTypeUpdateRequest = z
  .object({
    name: z.string().min(1).max(120).optional(),
    paid: z.boolean().optional(),
    quotaX10: z.number().int().min(0).max(3660).optional(),
    quotaPeriod: LeaveQuotaPeriod.optional(),
    active: z.boolean().optional(),
  })
  .refine(quotaFitsPeriod, {
    message: 'quota exceeds the length of the period',
    path: ['quotaX10'],
  });
export type LeaveTypeUpdateRequest = z.infer<typeof LeaveTypeUpdateRequest>;

export const LeaveTypeSummary = z.object({
  id: z.string(),
  name: z.string(),
  paid: z.boolean(),
  quotaX10: z.number().int(),
  quotaPeriod: LeaveQuotaPeriod,
  active: z.boolean(),
});
export type LeaveTypeSummary = z.infer<typeof LeaveTypeSummary>;

// ----- Holiday -----
export const HolidayCreateRequest = z.object({
  date: IsoDate7,
  name: z.string().min(1).max(160),
});
export type HolidayCreateRequest = z.infer<typeof HolidayCreateRequest>;

export const HolidaySummary = z.object({
  id: z.string(),
  date: z.string(),
  name: z.string(),
  active: z.boolean(),
});
export type HolidaySummary = z.infer<typeof HolidaySummary>;

// ----- Leave Request -----
export const LeaveRequestCreateRequest = z
  .object({
    personId: z.string().min(1),
    leaveTypeId: z.string().min(1),
    fromDate: IsoDate7,
    toDate: IsoDate7,
    halfDay: z.boolean().default(false),
    reason: z.string().max(500).optional(),
  })
  .refine((v) => v.toDate >= v.fromDate, {
    message: 'toDate must not be earlier than fromDate',
    path: ['toDate'],
  })
  .refine((v) => !v.halfDay || v.fromDate === v.toDate, {
    message: 'halfDay is only allowed when fromDate === toDate',
    path: ['halfDay'],
  });
export type LeaveRequestCreateRequest = z.infer<typeof LeaveRequestCreateRequest>;

export const LeaveRequestDecideRequest = z.object({
  note: z.string().max(500).optional(),
});
export type LeaveRequestDecideRequest = z.infer<typeof LeaveRequestDecideRequest>;

export const LeaveRequestCancelRequest = z.object({
  reason: z.string().min(1).max(500),
});
export type LeaveRequestCancelRequest = z.infer<typeof LeaveRequestCancelRequest>;

export const LeaveRequestSummary = z.object({
  id: z.string(),
  personId: z.string(),
  personName: z.string().nullable(),
  leaveTypeId: z.string(),
  leaveTypeName: z.string(),
  paid: z.boolean(),
  fromDate: z.string(),
  toDate: z.string(),
  halfDay: z.boolean(),
  daysX10: z.number().int(),
  reason: z.string().nullable(),
  status: LeaveStatus,
  approverId: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decisionNote: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  createdAt: z.string(),
});
export type LeaveRequestSummary = z.infer<typeof LeaveRequestSummary>;

export const LeaveBalance = z.object({
  quotaX10: z.number().int(),
  quotaPeriod: LeaveQuotaPeriod,
  /** Days already held by pending, approved or availed requests in the window. */
  usedX10: z.number().int(),
  /** Null when the type has no quota. */
  remainingX10: z.number().int().nullable(),
  windowLabel: z.string().nullable(),
});
export type LeaveBalance = z.infer<typeof LeaveBalance>;

// ----- Attendance Lock / paid-days -----
export const PaidDaysBreakdown = z.object({
  workingDaysX10: z.number().int(),
  holidayDaysX10: z.number().int(),
  weekendDaysX10: z.number().int(),
  paidLeaveDaysX10: z.number().int(),
  unpaidLeaveDaysX10: z.number().int(),
  lopDaysX10: z.number().int(),
  paidDaysX10: z.number().int(),
});
export type PaidDaysBreakdown = z.infer<typeof PaidDaysBreakdown>;

export const AttendanceRow = PaidDaysBreakdown.extend({
  personId: z.string(),
  personName: z.string().nullable(),
  joinDate: z.string(),
  exitDate: z.string().nullable(),
  locked: z.boolean(),
});
export type AttendanceRow = z.infer<typeof AttendanceRow>;

export const AttendanceMonthResponse = z.object({
  month: Month7,
  locked: z.boolean(),
  lockedAt: z.string().nullable(),
  lockedBy: z.string().nullable(),
  pendingApprovals: z.number().int(),
  rows: z.array(AttendanceRow),
});
export type AttendanceMonthResponse = z.infer<typeof AttendanceMonthResponse>;

export const AttendanceCalendarCell = z.object({
  date: z.string(),
  // What the day was, most specific first: an explicit attendance ruling beats a
  // leave, which beats the shape of the calendar itself.
  kind: z.enum([
    'present',
    'absent',
    'excused',
    'lop',
    'leave_paid',
    'leave_unpaid',
    'leave_half',
    'leave_pending',
    'holiday',
    'weekend',
    'outside_employment',
    'working',
  ]),
  label: z.string().nullable(),
  workedMinutes: z.number().int().nullable(),
});
export type AttendanceCalendarCell = z.infer<typeof AttendanceCalendarCell>;

export const AttendanceCalendarPerson = z.object({
  personId: z.string(),
  personName: z.string().nullable(),
  days: z.array(AttendanceCalendarCell),
});
export type AttendanceCalendarPerson = z.infer<typeof AttendanceCalendarPerson>;

export const AttendanceCalendarResponse = z.object({
  month: Month7,
  dates: z.array(z.string()),
  holidays: z.array(z.object({ date: z.string(), name: z.string() })),
  people: z.array(AttendanceCalendarPerson),
});
export type AttendanceCalendarResponse = z.infer<typeof AttendanceCalendarResponse>;

export const AttendanceLockRequest = z.object({});
export type AttendanceLockRequest = z.infer<typeof AttendanceLockRequest>;

export const AttendanceUnlockRequest = z.object({
  reason: z.string().min(1).max(500),
});
export type AttendanceUnlockRequest = z.infer<typeof AttendanceUnlockRequest>;

export const AttendanceLockResponse = z.object({
  month: Month7,
  personCount: z.number().int(),
  totalPaidDaysX10: z.number().int(),
  totalLopDaysX10: z.number().int(),
});
export type AttendanceLockResponse = z.infer<typeof AttendanceLockResponse>;

// ============================================================
// M8 — Payroll
// ============================================================

const IsoDate8 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const Month8 = z.string().regex(/^\d{4}-\d{2}$/);

export const PayrollRunKind = z.enum(['regular', 'off_cycle']);
export type PayrollRunKind = z.infer<typeof PayrollRunKind>;

export const PayrollRunStatus = z.enum(['draft', 'preview', 'locked', 'posted']);
export type PayrollRunStatus = z.infer<typeof PayrollRunStatus>;

export const StatutoryKind = z.enum(['pf', 'esi', 'pt', 'tds']);
export type StatutoryKind = z.infer<typeof StatutoryKind>;

// ----- Person rate -----
export const PersonRateCreateRequest = z.object({
  personId: z.string().min(1),
  monthlyCostPaise: z.number().int().min(0),
  validFrom: IsoDate8,
});
export type PersonRateCreateRequest = z.infer<typeof PersonRateCreateRequest>;

export const PersonRateSummary = z.object({
  id: z.string(),
  personId: z.string(),
  personName: z.string().nullable(),
  monthlyCostPaise: z.number().int(),
  validFrom: z.string(),
  createdAt: z.string(),
});
export type PersonRateSummary = z.infer<typeof PersonRateSummary>;

// ----- Salary structure -----
export const StructureComponent = z.object({
  name: z.string().min(1).max(120),
  basis: z.enum(['percent_of_gross_bpp', 'flat_paise']),
  value: z.number().int().min(0),
});
export type StructureComponent = z.infer<typeof StructureComponent>;

export const SalaryStructureCreateRequest = z.object({
  personId: z.string().min(1),
  validFrom: IsoDate8,
  components: z.array(StructureComponent).min(1),
});
export type SalaryStructureCreateRequest = z.infer<typeof SalaryStructureCreateRequest>;

export const SalaryStructureSummary = z.object({
  id: z.string(),
  personId: z.string(),
  personName: z.string().nullable(),
  validFrom: z.string(),
  components: z.array(StructureComponent),
  createdAt: z.string(),
});
export type SalaryStructureSummary = z.infer<typeof SalaryStructureSummary>;

// ----- Component template -----
export const ComponentTemplateCreateRequest = z.object({
  name: z.string().min(1).max(120),
  components: z.array(StructureComponent).min(1),
});
export type ComponentTemplateCreateRequest = z.infer<typeof ComponentTemplateCreateRequest>;

export const ComponentTemplateSummary = z.object({
  id: z.string(),
  name: z.string(),
  components: z.array(StructureComponent),
  active: z.boolean(),
});
export type ComponentTemplateSummary = z.infer<typeof ComponentTemplateSummary>;

// ----- Statutory config -----
export const StatutoryConfigCreateRequest = z.object({
  kind: StatutoryKind,
  jurisdiction: z.string().min(1).max(64),
  params: z.record(z.unknown()),
  validFrom: IsoDate8,
});
export type StatutoryConfigCreateRequest = z.infer<typeof StatutoryConfigCreateRequest>;

export const StatutoryConfigVerifyRequest = z.object({
  reference: z.string().min(1).max(500),
});
export type StatutoryConfigVerifyRequest = z.infer<typeof StatutoryConfigVerifyRequest>;

export const StatutoryConfigSummary = z.object({
  id: z.string(),
  kind: StatutoryKind,
  jurisdiction: z.string(),
  params: z.record(z.unknown()),
  validFrom: z.string(),
  verifiedBy: z.string().nullable(),
  verifiedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type StatutoryConfigSummary = z.infer<typeof StatutoryConfigSummary>;

// ----- Payroll run -----
export const PayrollRunCreateRequest = z.object({
  month: Month8,
  kind: PayrollRunKind.default('regular'),
  notes: z.string().max(500).optional(),
});
export type PayrollRunCreateRequest = z.infer<typeof PayrollRunCreateRequest>;

export const PayrollRunSummary = z.object({
  id: z.string(),
  month: Month8,
  kind: PayrollRunKind,
  status: PayrollRunStatus,
  itemCount: z.number().int(),
  totalGrossPaise: z.number().int(),
  totalNetPaise: z.number().int(),
  totalEmployerCostPaise: z.number().int(),
  previewedAt: z.string().nullable(),
  lockedAt: z.string().nullable(),
  postedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type PayrollRunSummary = z.infer<typeof PayrollRunSummary>;

export const PayrollComponentEntry = z.object({
  name: z.string(),
  amountPaise: z.number().int(),
});
export type PayrollComponentEntry = z.infer<typeof PayrollComponentEntry>;

export const PayrollItemSummary = z.object({
  id: z.string(),
  personId: z.string(),
  personName: z.string().nullable(),
  paidDaysX10: z.number().int(),
  grossPaise: z.number().int(),
  components: z.array(PayrollComponentEntry),
  deductions: z.array(PayrollComponentEntry),
  netPaise: z.number().int(),
  employerCostPaise: z.number().int(),
});
export type PayrollItemSummary = z.infer<typeof PayrollItemSummary>;

export const PayslipListEntry = z.object({
  runId: z.string(),
  itemId: z.string(),
  month: z.string(),
  kind: PayrollRunKind,
  status: PayrollRunStatus,
  netPaise: z.number().int(),
  postedAt: z.string().nullable(),
});
export type PayslipListEntry = z.infer<typeof PayslipListEntry>;

export const PayrollAllocationSummary = z.object({
  personId: z.string(),
  projectId: z.string(),
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  amountPaise: z.number().int(),
});
export type PayrollAllocationSummary = z.infer<typeof PayrollAllocationSummary>;

export const PayrollRunDetail = PayrollRunSummary.extend({
  notes: z.string().nullable(),
  items: z.array(PayrollItemSummary),
  allocations: z.array(PayrollAllocationSummary),
});
export type PayrollRunDetail = z.infer<typeof PayrollRunDetail>;

export const EmptyBody = z.object({});
export type EmptyBody = z.infer<typeof EmptyBody>;

// ============================================================
// M9 — Ideas & Initiatives
// ============================================================

const IsoDate9 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const IdeaCategory = z.enum(['revenue', 'partnership', 'cost_saving', 'process', 'product']);
export type IdeaCategory = z.infer<typeof IdeaCategory>;

export const IdeaStatus = z.enum([
  'draft',
  'proposed',
  'under_review',
  'approved',
  'rejected',
  'parked',
]);
export type IdeaStatus = z.infer<typeof IdeaStatus>;

export const IdeaVisibility = z.enum(['c_suite_and_sponsor', 'all_staff']);
export type IdeaVisibility = z.infer<typeof IdeaVisibility>;

export const IdeaCreateRequest = z.object({
  title: z.string().min(1).max(240),
  pitch: z.string().min(1).max(10_000),
  category: IdeaCategory,
  sponsorId: z.string().min(1).optional(),
  visibility: IdeaVisibility.default('c_suite_and_sponsor'),
});
export type IdeaCreateRequest = z.infer<typeof IdeaCreateRequest>;

export const IdeaProposeRequest = z.object({
  rationale: z.string().min(1).max(2000),
  decisionOwnerId: z.string().min(1).optional(),
  decisionDueOn: IsoDate9.optional(),
});
export type IdeaProposeRequest = z.infer<typeof IdeaProposeRequest>;

export const IdeaPickUpRequest = z.object({
  rationale: z.string().min(1).max(2000),
  decisionOwnerId: z.string().min(1),
  decisionDueOn: IsoDate9,
});
export type IdeaPickUpRequest = z.infer<typeof IdeaPickUpRequest>;

export const IdeaParkRequest = z.object({
  rationale: z.string().min(1).max(2000),
  revisitOn: IsoDate9,
});
export type IdeaParkRequest = z.infer<typeof IdeaParkRequest>;

export const IdeaRejectRequest = z.object({
  rationale: z.string().min(1).max(2000),
});
export type IdeaRejectRequest = z.infer<typeof IdeaRejectRequest>;

export const IdeaApproveRequest = z.object({
  rationale: z.string().min(1).max(2000),
  projectCode: z
    .string()
    .min(1)
    .max(32)
    .regex(/^[A-Z0-9._-]+$/i, 'code must be alphanumeric + . _ -'),
  projectName: z.string().min(1).max(240),
  budgetPaise: z.number().int().positive(),
  budgetReason: z.string().min(1).max(500),
  reviewOffsetDays: z.number().int().min(1).max(365).default(90),
});
export type IdeaApproveRequest = z.infer<typeof IdeaApproveRequest>;

export const IdeaResurfaceRequest = z.object({
  rationale: z.string().min(1).max(2000),
});
export type IdeaResurfaceRequest = z.infer<typeof IdeaResurfaceRequest>;

export const IdeaDecisionSummary = z.object({
  id: z.string(),
  kind: z.enum([
    'proposed',
    'picked_up_for_review',
    'parked',
    'resurfaced',
    'approved',
    'rejected',
  ]),
  fromStatus: IdeaStatus,
  toStatus: IdeaStatus,
  rationale: z.string(),
  decidedBy: z.string(),
  decidedByName: z.string().nullable(),
  createdAt: z.string(),
});
export type IdeaDecisionSummary = z.infer<typeof IdeaDecisionSummary>;

export const IdeaSummary = z.object({
  id: z.string(),
  title: z.string(),
  category: IdeaCategory,
  status: IdeaStatus,
  sponsorId: z.string(),
  sponsorName: z.string().nullable(),
  decisionOwnerId: z.string().nullable(),
  decisionDueOn: z.string().nullable(),
  revisitOn: z.string().nullable(),
  visibility: IdeaVisibility,
  convertedProjectId: z.string().nullable(),
  voteCount: z.number().int(),
  commentCount: z.number().int(),
  createdAt: z.string(),
});
export type IdeaSummary = z.infer<typeof IdeaSummary>;

export const IdeaDetail = IdeaSummary.extend({
  pitch: z.string(),
  decisions: z.array(IdeaDecisionSummary),
  votedByMe: z.boolean(),
});
export type IdeaDetail = z.infer<typeof IdeaDetail>;

export const CommentCreateRequest = z.object({
  body: z.string().min(1).max(5_000),
  parentId: z.string().optional(),
});
export type CommentCreateRequest = z.infer<typeof CommentCreateRequest>;

export const MentionSummary = z.object({
  personId: z.string(),
  personName: z.string().nullable(),
});
export type MentionSummary = z.infer<typeof MentionSummary>;

export const CommentSummary = z.object({
  id: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  authorId: z.string(),
  authorName: z.string().nullable(),
  body: z.string(),
  parentId: z.string().nullable(),
  createdAt: z.string(),
  mentions: z.array(MentionSummary),
});
export type CommentSummary = z.infer<typeof CommentSummary>;

export const VoteToggleResult = z.object({
  voted: z.boolean(),
  voteCount: z.number().int(),
});
export type VoteToggleResult = z.infer<typeof VoteToggleResult>;

export const IdeaApproveResult = z.object({
  ideaId: z.string(),
  projectId: z.string(),
});
export type IdeaApproveResult = z.infer<typeof IdeaApproveResult>;

// ============================================================
// M10 — Notifications & Queues polish
// ============================================================

export const NotificationPreferences = z.object({
  digestHourIst: z.number().int().min(0).max(23),
  immediateKinds: z.array(z.string().min(1).max(64)),
});
export type NotificationPreferences = z.infer<typeof NotificationPreferences>;

export const NotificationPreferencesUpdateRequest = NotificationPreferences.partial();
export type NotificationPreferencesUpdateRequest = z.infer<
  typeof NotificationPreferencesUpdateRequest
>;

export const QueueItemSummary = z.object({
  id: z.string(),
  kind: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  dueAt: z.string().nullable(),
  slaBreachedAt: z.string().nullable(),
  createdAt: z.string(),
  deepLink: z.string(),
  label: z.string(),
});
export type QueueItemSummary = z.infer<typeof QueueItemSummary>;

export const QueueItemsResponse = z.object({
  items: z.array(QueueItemSummary),
  byKind: z.record(z.number().int()),
  total: z.number().int(),
});
export type QueueItemsResponse = z.infer<typeof QueueItemsResponse>;

// ============================================================
// M12 — Analytics & Metrics Dictionary
// ============================================================

export const MetricBasis = z.enum([
  'cash',
  'accrual',
  'mixed',
  'allocation',
  'documents_and_receipts',
  'ledger',
  'survey',
]);
export type MetricBasis = z.infer<typeof MetricBasis>;

export const MetricEntry = z.object({
  id: z.string(),
  name: z.string(),
  formula: z.string(),
  basis: MetricBasis,
  description: z.string(),
  drillTo: z.string().nullable(),
});
export type MetricEntry = z.infer<typeof MetricEntry>;

/**
 * The metrics dictionary is the source of truth for every KPI card. New metrics
 * MUST land here so the dashboard cards can link to a definition.
 */
export const METRICS: readonly MetricEntry[] = [
  {
    id: 'project_pnl',
    name: 'Project P&L',
    formula: 'issued documents − (payroll allocation + mapped expenses)',
    basis: 'accrual',
    description:
      'Per-project accrual profit. Issued (non-draft) invoices credit; posted payroll allocations and mapped ledger expenses debit.',
    drillTo: '/projects',
  },
  {
    id: 'budget_variance',
    name: 'Budget variance',
    formula: 'actual − effective budget revision',
    basis: 'accrual',
    description:
      'Actual costs minus the effective BudgetRevision for the project as of the reporting date.',
    drillTo: '/projects',
  },
  {
    id: 'utilization',
    name: 'Utilization',
    formula: 'Σ allocation % on billable projects ÷ capacity',
    basis: 'allocation',
    description:
      "Fraction of each person's monthly capacity assigned to client_delivery projects (via the allocation matrix). 100% = fully billable.",
    drillTo: '/allocations',
  },
  {
    id: 'dso',
    name: 'DSO (invoice turnaround)',
    formula: 'avg(payment date − issue date) across paid documents',
    basis: 'documents_and_receipts',
    description:
      'Days-sales-outstanding: average days between an invoice being issued and being fully paid. Only paid documents are counted.',
    drillTo: '/billing/documents',
  },
  {
    id: 'reconciliation_lag',
    name: 'Reconciliation lag',
    formula: 'avg(mapped date − transaction business date)',
    basis: 'ledger',
    description:
      "Time between a transaction's business date and when it was first mapped to a project or cost center. Lower is better.",
    drillTo: '/ledger/transactions',
  },
  {
    id: 'payroll_cost_ratio',
    name: 'Payroll cost ratio',
    formula: 'posted payroll ÷ accrual revenue',
    basis: 'mixed',
    description:
      'For the given month, employer-cost of every posted regular payroll run divided by issued document totals in the same month.',
    drillTo: '/payroll',
  },
  {
    id: 'cac',
    name: 'CAC (per channel)',
    formula: 'channel spend in period ÷ new clients with that first-touch channel',
    basis: 'cash',
    description: 'Acquisition cost per client for each lead channel over the chosen window.',
    drillTo: '/analytics/economics',
  },
  {
    id: 'ltv',
    name: 'LTV',
    formula: 'Σ gross margin per client, trailing 36 months',
    basis: 'accrual',
    description:
      "Issued documents minus payroll allocations and mapped expenses on the client's projects.",
    drillTo: '/analytics/economics',
  },
  {
    id: 'payback_client',
    name: 'Payback (client)',
    formula: 'months until cumulative client margin ≥ CAC share',
    basis: 'mixed',
    description:
      "CAC share is the channel CAC in the client's acquisition month; margin is accrual.",
    drillTo: '/analytics/economics',
  },
  {
    id: 'npv_irr',
    name: 'NPV / IRR',
    formula: 'discounted mapped project cash in/out at a configurable annual rate',
    basis: 'cash',
    description: 'Monthly net flows per project. IRR is annualised from the monthly root.',
    drillTo: '/analytics/economics',
  },
  {
    id: 'tco',
    name: 'TCO (per project)',
    formula: 'posted payroll + mapped expenses + pro-rata share of overhead',
    basis: 'accrual',
    description: "Overhead is cost-center-mapped spend, spread by each project's direct cost.",
    drillTo: '/analytics/economics',
  },
  {
    id: 'nps',
    name: 'NPS',
    formula: '% promoters (9–10) − % detractors (0–6) across answered project-close surveys',
    basis: 'survey',
    description:
      'Standard Net Promoter Score from the tokenised survey sent to the client at project close. Passives (7–8) count toward the total but not the score.',
    drillTo: '/projects',
  },
  {
    id: 'ar_aging',
    name: 'AR aging',
    formula: 'outstanding invoice balance × age buckets',
    basis: 'documents_and_receipts',
    description:
      'Open receivables split into current / 0-30 / 31-60 / 61-90 / 90+ day buckets. See the billing AR aging page.',
    drillTo: '/billing/ar-aging',
  },
  {
    id: 'cac',
    name: 'CAC (per channel)',
    formula: 'channel spend in period ÷ new clients with that first-touch channel',
    basis: 'cash',
    description:
      'Customer-acquisition cost per lead channel — channel spend divided by count of clients whose first-touch channel matches.',
    drillTo: '/crm/channels',
  },
  {
    id: 'ltv',
    name: 'LTV',
    formula: 'Σ gross margin per client, trailing 36 mo',
    basis: 'accrual',
    description: 'Lifetime value per client — sum of gross margin across the trailing window.',
    drillTo: '/crm/clients',
  },
  {
    id: 'payback_client',
    name: 'Payback (client)',
    formula: 'months until cumulative client margin ≥ CAC share',
    basis: 'mixed',
    description: 'How long a client takes to earn back their share of channel spend.',
    drillTo: '/crm/clients',
  },
  {
    id: 'payback_initiative',
    name: 'Payback (initiative)',
    formula: 'months until cumulative initiative net cash ≥ 0',
    basis: 'cash',
    description: 'For internal initiative projects, months until net cash flow turns positive.',
    drillTo: '/ideas',
  },
  {
    id: 'decision_latency',
    name: 'Decision latency',
    formula: 'avg(decision date − proposed date)',
    basis: 'accrual',
    description: 'Average days between an idea being proposed and receiving a decision.',
    drillTo: '/ideas',
  },
];

// ----- Data-health tiles -----
export const DataHealthTile = z.object({
  id: z.string(),
  label: z.string(),
  count: z.number().int(),
  severity: z.enum(['ok', 'warn', 'alert']),
  deepLink: z.string(),
  description: z.string(),
});
export type DataHealthTile = z.infer<typeof DataHealthTile>;

export const DataHealthResponse = z.object({
  asOf: z.string(),
  tiles: z.array(DataHealthTile),
});
export type DataHealthResponse = z.infer<typeof DataHealthResponse>;

// ----- Point-in-time metric responses -----
const IsoDate12 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const Month12 = z.string().regex(/^\d{4}-\d{2}$/);

export const ProjectPnlRow = z.object({
  projectId: z.string(),
  code: z.string(),
  name: z.string(),
  type: z.enum(['client_delivery', 'internal_initiative']),
  revenuePaise: z.number().int(),
  payrollCostPaise: z.number().int(),
  expenseCostPaise: z.number().int(),
  marginPaise: z.number().int(),
});
export type ProjectPnlRow = z.infer<typeof ProjectPnlRow>;

export const ProjectPnlResponse = z.object({
  asOf: IsoDate12,
  rows: z.array(ProjectPnlRow),
  totals: z.object({
    revenuePaise: z.number().int(),
    payrollCostPaise: z.number().int(),
    expenseCostPaise: z.number().int(),
    marginPaise: z.number().int(),
  }),
});
export type ProjectPnlResponse = z.infer<typeof ProjectPnlResponse>;

export const DsoResponse = z.object({
  asOf: IsoDate12,
  avgDays: z.number().nullable(),
  paidDocumentCount: z.number().int(),
});
export type DsoResponse = z.infer<typeof DsoResponse>;

export const ReconciliationLagResponse = z.object({
  asOf: IsoDate12,
  avgDays: z.number().nullable(),
  reconciledCount: z.number().int(),
});
export type ReconciliationLagResponse = z.infer<typeof ReconciliationLagResponse>;

export const PayrollCostRatioResponse = z.object({
  month: Month12,
  payrollCostPaise: z.number().int(),
  revenuePaise: z.number().int(),
  ratio: z.number().nullable(),
});
export type PayrollCostRatioResponse = z.infer<typeof PayrollCostRatioResponse>;

export const UtilizationRow = z.object({
  personId: z.string(),
  personName: z.string().nullable(),
  billablePct: z.number().int(),
  nonBillablePct: z.number().int(),
  unallocatedPct: z.number().int(),
});
export type UtilizationRow = z.infer<typeof UtilizationRow>;

export const UtilizationResponse = z.object({
  month: Month12,
  rows: z.array(UtilizationRow),
  avgBillablePct: z.number(),
});
export type UtilizationResponse = z.infer<typeof UtilizationResponse>;

export const PersonKpiRow = z.object({
  personId: z.string(),
  personName: z.string().nullable(),
  employeeId: z.string().nullable(),
  presentDays: z.number().int(),
  absentDays: z.number().int(),
  excusedDays: z.number().int(),
  lopDays: z.number().int(),
  leaveDaysX10: z.number().int(),
  workedMinutes: z.number().int(),
  avgMinutesPerPresentDay: z.number().int(),
  openTasks: z.number().int(),
  overdueTasks: z.number().int(),
  completedTasks: z.number().int(),
  completedOnTime: z.number().int(),
  /** Minutes declared against tasks on client-delivery projects. */
  billableMinutes: z.number().int(),
  /** Minutes declared against tasks on internal initiatives. */
  nonBillableMinutes: z.number().int(),
  /** Minutes logged with no task, or against a task whose project is unknown. */
  unmappedMinutes: z.number().int(),
  /** Shares of all minutes declared in the month; 0 when nothing was logged. */
  billablePct: z.number().int(),
  nonBillablePct: z.number().int(),
  unmappedPct: z.number().int(),
});
export type PersonKpiRow = z.infer<typeof PersonKpiRow>;

export const PeopleKpiResponse = z.object({
  month: Month12,
  rows: z.array(PersonKpiRow),
});
export type PeopleKpiResponse = z.infer<typeof PeopleKpiResponse>;

export const BudgetVarianceRow = z.object({
  projectId: z.string(),
  code: z.string(),
  name: z.string(),
  budgetPaise: z.number().int(),
  actualPaise: z.number().int(),
  variancePaise: z.number().int(),
});
export type BudgetVarianceRow = z.infer<typeof BudgetVarianceRow>;

export const BudgetVarianceResponse = z.object({
  asOf: IsoDate12,
  rows: z.array(BudgetVarianceRow),
});
export type BudgetVarianceResponse = z.infer<typeof BudgetVarianceResponse>;

// ============================================================
// Sales pipeline (Prospects, Activities, Service catalog, C-suite)
// ============================================================

export const CsuiteRole = z.enum(['ceo', 'coo', 'cto', 'cfo', 'cmo', 'other']);
export type CsuiteRole = z.infer<typeof CsuiteRole>;

export const ProspectStage = z.enum([
  'lead',
  'contacted',
  'proposal_sent',
  'negotiation',
  'won',
  'lost',
]);
export type ProspectStage = z.infer<typeof ProspectStage>;

export const ProspectStatus = z.enum(['active', 'dormant', 'archived']);
export type ProspectStatus = z.infer<typeof ProspectStatus>;

export const SalesActivityKind = z.enum([
  'call',
  'meeting',
  'demo',
  'proposal',
  'follow_up',
  'email',
  'other',
]);
export type SalesActivityKind = z.infer<typeof SalesActivityKind>;

export const SalesActivityOutcome = z.enum(['positive', 'neutral', 'negative', 'blocked']);
export type SalesActivityOutcome = z.infer<typeof SalesActivityOutcome>;

// ----- Service catalog -----
export const ServiceOfferingUpsertRequest = z.object({
  name: z.string().min(1).max(120),
  active: z.boolean().default(true),
  sortOrder: z.number().int().min(0).default(0),
});
export type ServiceOfferingUpsertRequest = z.infer<typeof ServiceOfferingUpsertRequest>;

export const ServiceOfferingSummary = z.object({
  id: z.string(),
  name: z.string(),
  active: z.boolean(),
  sortOrder: z.number().int(),
});
export type ServiceOfferingSummary = z.infer<typeof ServiceOfferingSummary>;

// ----- Prospect -----
export const ProspectCreateRequest = z.object({
  displayName: z.string().min(1).max(240),
  primaryContactName: z.string().max(160).optional(),
  primaryContactEmail: z.string().email().optional(),
  primaryContactPhone: z.string().max(32).optional(),
  leadChannelId: z.string().min(1),
  ownerId: z.string().min(1),
  serviceOfferingIds: z.array(z.string()).default([]),
  estimatedValuePaise: z.number().int().nonnegative().optional(),
  expectedCloseDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  nextTouchDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  currentClientId: z.string().optional(),
  notes: z.string().optional(),
});
export type ProspectCreateRequest = z.infer<typeof ProspectCreateRequest>;

export const ClientConversionPayload = z.object({
  legalName: z.string().min(1).max(240),
  gstin: z.string().length(15).optional(),
  gstTreatment: GstTreatment,
  pan: z
    .string()
    .regex(/^[A-Z]{5}\d{4}[A-Z]$/)
    .optional(),
  billingAddress: z.string().min(1),
  placeOfSupplyState: z.string().min(1).max(64),
  paymentTermsDays: z.number().int().min(0).max(365).default(30),
  force: z.boolean().default(false),
});
export type ClientConversionPayload = z.infer<typeof ClientConversionPayload>;

export const ProspectStageTransitionRequest = z.object({
  toStage: ProspectStage,
  lostReason: z.string().max(500).optional(),
  clientConversion: ClientConversionPayload.optional(),
});
export type ProspectStageTransitionRequest = z.infer<typeof ProspectStageTransitionRequest>;

export const ProspectStageCorrectionRequest = z.object({
  toStage: ProspectStage,
  reason: z.string().min(1).max(500),
});
export type ProspectStageCorrectionRequest = z.infer<typeof ProspectStageCorrectionRequest>;

export const ProspectTransitionResponse = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('transitioned'),
    convertedClientId: z.string().optional(),
  }),
  z.object({
    status: z.literal('dedupe_suggestions'),
    suggestions: z.array(DedupeSuggestion),
  }),
]);
export type ProspectTransitionResponse = z.infer<typeof ProspectTransitionResponse>;

export const ProspectUpdateRequest = z.object({
  displayName: z.string().min(1).max(240).optional(),
  primaryContactName: z.string().max(160).nullable().optional(),
  primaryContactEmail: z.string().email().nullable().optional(),
  primaryContactPhone: z.string().max(32).nullable().optional(),
  ownerId: z.string().optional(),
  serviceOfferingIds: z.array(z.string()).optional(),
  estimatedValuePaise: z.number().int().nonnegative().nullable().optional(),
  expectedCloseDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  nextTouchDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  notes: z.string().nullable().optional(),
  status: ProspectStatus.optional(),
});
export type ProspectUpdateRequest = z.infer<typeof ProspectUpdateRequest>;

export const ProspectSummary = z.object({
  id: z.string(),
  displayName: z.string(),
  primaryContactName: z.string().nullable(),
  ownerId: z.string(),
  ownerName: z.string(),
  stage: ProspectStage,
  status: ProspectStatus,
  leadChannelId: z.string(),
  leadChannelName: z.string(),
  estimatedValuePaise: z.number().int().nullable(),
  expectedCloseDate: z.string().nullable(),
  nextTouchDate: z.string().nullable(),
  currentClientId: z.string().nullable(),
  convertedClientId: z.string().nullable(),
  isStale: z.boolean(),
  lastActivityAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ProspectSummary = z.infer<typeof ProspectSummary>;

export const ProspectDetail = ProspectSummary.extend({
  primaryContactEmail: z.string().nullable(),
  primaryContactPhone: z.string().nullable(),
  notes: z.string().nullable(),
  lostReason: z.string().nullable(),
  wonAt: z.string().nullable(),
  lostAt: z.string().nullable(),
  interests: z.array(ServiceOfferingSummary),
});
export type ProspectDetail = z.infer<typeof ProspectDetail>;

// ----- Sales activity -----
export const SalesActivityCreateRequest = z.object({
  kind: SalesActivityKind,
  occurredAt: z.string(),
  durationMinutes: z
    .number()
    .int()
    .min(0)
    .max(24 * 60)
    .optional(),
  summary: z.string().min(1).max(4000),
  outcome: SalesActivityOutcome.optional(),
});
export type SalesActivityCreateRequest = z.infer<typeof SalesActivityCreateRequest>;

export const SalesActivitySummary = z.object({
  id: z.string(),
  prospectId: z.string(),
  kind: SalesActivityKind,
  occurredAt: z.string(),
  durationMinutes: z.number().int().nullable(),
  summary: z.string(),
  outcome: SalesActivityOutcome.nullable(),
  performedById: z.string(),
  performedByName: z.string(),
  createdAt: z.string(),
});
export type SalesActivitySummary = z.infer<typeof SalesActivitySummary>;

// ----- Filters + dashboard -----
export const ProspectListFilters = z.object({
  ownerId: z.string().optional(),
  stage: ProspectStage.optional(),
  status: ProspectStatus.optional(),
  leadChannelId: z.string().optional(),
  serviceOfferingId: z.string().optional(),
  staleOnly: z.coerce.boolean().optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type ProspectListFilters = z.infer<typeof ProspectListFilters>;

export const SalesDashboardWindow = z.enum(['7d', '30d', '90d']);
export type SalesDashboardWindow = z.infer<typeof SalesDashboardWindow>;

export const OwnerScorecard = z.object({
  personId: z.string(),
  personName: z.string(),
  isCsuite: z.boolean(),
  csuiteRole: CsuiteRole.nullable(),
  openProspects: z.number().int(),
  activitiesInWindow: z.number().int(),
  wonInWindow: z.number().int(),
  lostInWindow: z.number().int(),
  conversionPct: z.number(),
  staleCount: z.number().int(),
  pipelineValuePaise: z.number().int(),
  wonValuePaise: z.number().int(),
});
export type OwnerScorecard = z.infer<typeof OwnerScorecard>;

export const StageBucketMetric = z.object({
  stage: ProspectStage,
  count: z.number().int(),
  valuePaise: z.number().int(),
});
export type StageBucketMetric = z.infer<typeof StageBucketMetric>;

export const ChannelAttributionRow = z.object({
  channelId: z.string(),
  channelName: z.string(),
  prospects: z.number().int(),
  won: z.number().int(),
  lost: z.number().int(),
  conversionPct: z.number(),
  wonValuePaise: z.number().int(),
});
export type ChannelAttributionRow = z.infer<typeof ChannelAttributionRow>;

export const SalesDashboardResponse = z.object({
  window: SalesDashboardWindow,
  owners: z.array(OwnerScorecard),
  stages: z.array(StageBucketMetric),
  channelAttribution: z.array(ChannelAttributionRow),
  staleTotal: z.number().int(),
  generatedAt: z.string(),
});
export type SalesDashboardResponse = z.infer<typeof SalesDashboardResponse>;

// ----- C-suite member admin -----
export const CsuiteMemberUpsertRequest = z.object({
  personId: z.string().min(1),
  isCsuite: z.boolean(),
  csuiteRole: CsuiteRole.nullable(),
});
export type CsuiteMemberUpsertRequest = z.infer<typeof CsuiteMemberUpsertRequest>;

export const CsuiteMemberSummary = z.object({
  personId: z.string(),
  personName: z.string(),
  email: z.string(),
  isCsuite: z.boolean(),
  csuiteRole: CsuiteRole.nullable(),
});
export type CsuiteMemberSummary = z.infer<typeof CsuiteMemberSummary>;

// ============================================================
// Personal Access Tokens (for MCP / API integrations)
// ============================================================

export const ApiTokenScope = z.enum([
  'read',
  'write:sales',
  'write:crm',
  'write:ideas',
  'write:notifications',
]);
export type ApiTokenScope = z.infer<typeof ApiTokenScope>;

export const ApiTokenCreateRequest = z.object({
  name: z.string().min(1).max(120),
  scopes: z.array(ApiTokenScope).min(1),
  expiresInDays: z.number().int().min(1).max(365).optional(),
});
export type ApiTokenCreateRequest = z.infer<typeof ApiTokenCreateRequest>;

export const ApiTokenCreateResponse = z.object({
  id: z.string(),
  token: z.string(),
});
export type ApiTokenCreateResponse = z.infer<typeof ApiTokenCreateResponse>;

export const ApiTokenSummary = z.object({
  id: z.string(),
  name: z.string(),
  scopes: z.array(ApiTokenScope),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type ApiTokenSummary = z.infer<typeof ApiTokenSummary>;

// ============================================================
// People & Access (admin management of Person + Role + 2FA)
// ============================================================

export const PersonDetail = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  employeeId: z.string().nullable(),
  phone: z.string().nullable(),
  status: z.enum(['active', 'exited']),
  dateJoined: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateLeft: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  roles: z.array(RoleName),
  isCsuite: z.boolean(),
  csuiteRole: CsuiteRole.nullable(),
  twoFactorVerified: z.boolean(),
  mustChangePassword: z.boolean(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  source: z.string(),
});
export type PersonDetail = z.infer<typeof PersonDetail>;

export const PersonListResponse = z.array(PersonDetail);
export type PersonListResponse = z.infer<typeof PersonListResponse>;

export const PersonUpdateRequest = z.object({
  name: z.string().min(1).max(160).optional(),
  email: z.string().email().max(160).optional(),
  phone: z.string().max(32).nullable().optional(),
  dateJoined: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  employeeId: z
    .string()
    .trim()
    .min(1)
    .max(32)
    .transform((v) => v.toUpperCase())
    .optional(),
});
export type PersonUpdateRequest = z.infer<typeof PersonUpdateRequest>;

export const PersonSetRolesRequest = z.object({
  roles: z.array(RoleName).min(1),
});
export type PersonSetRolesRequest = z.infer<typeof PersonSetRolesRequest>;

export const PersonResetPasswordRequest = z.object({
  newPassword: z.string().min(8).max(200),
});
export type PersonResetPasswordRequest = z.infer<typeof PersonResetPasswordRequest>;

export const PersonExitRequest = z.object({
  dateLeft: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(1).max(500),
});
export type PersonExitRequest = z.infer<typeof PersonExitRequest>;

// Employee IDs read <prefix><join year><per-year sequence>, e.g. BITS230013.
export const EmployeeIdFormatCreateRequest = z.object({
  prefix: z
    .string()
    .trim()
    .regex(/^[A-Za-z][A-Za-z0-9]{0,15}$/)
    .transform((v) => v.toUpperCase()),
  yearDigits: z.union([z.literal(2), z.literal(4)]),
  seqDigits: z.number().int().min(2).max(6),
  validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type EmployeeIdFormatCreateRequest = z.infer<typeof EmployeeIdFormatCreateRequest>;

export const EmployeeIdFormatView = z.object({
  id: z.string(),
  prefix: z.string(),
  yearDigits: z.union([z.literal(2), z.literal(4)]),
  seqDigits: z.number().int(),
  validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  example: z.string(),
  isCurrent: z.boolean(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
});
export type EmployeeIdFormatView = z.infer<typeof EmployeeIdFormatView>;

export const EmployeeIdFormatListResponse = z.array(EmployeeIdFormatView);

// ----- Work log (M13, spec §7.3.1) -----
export const WorkSessionStatus = z.enum(['open', 'closed', 'auto_closed']);
export type WorkSessionStatus = z.infer<typeof WorkSessionStatus>;

export const TaskWorkOutcome = z.enum(['completed', 'pending']);
export type TaskWorkOutcome = z.infer<typeof TaskWorkOutcome>;

export const ClockInRequest = z
  .object({
    taskIds: z.array(z.string().min(1)).default([]),
    // Starting without a task stays allowed (spec §7.3.1) but must be chosen,
    // not arrived at by submitting an empty form.
    noTask: z.boolean().default(false),
  })
  .refine((v) => (v.noTask ? v.taskIds.length === 0 : v.taskIds.length > 0), {
    message: 'Pick at least one task, or choose "No task" — not both, not neither.',
    path: ['taskIds'],
  });
export type ClockInRequest = z.infer<typeof ClockInRequest>;

export const AbsenceRulingRequest = z.object({
  note: z.string().max(500).optional(),
});
export type AbsenceRulingRequest = z.infer<typeof AbsenceRulingRequest>;

export const AbsenceRuling = z.enum(['lop', 'excuse', 'allow-backfill']);
export type AbsenceRuling = z.infer<typeof AbsenceRuling>;

export const AbsencePersonSummary = z.object({
  personId: z.string(),
  personName: z.string().nullable(),
  openDays: z.number().int(),
  backfillGranted: z.number().int(),
  oldest: z.string(),
  newest: z.string(),
});
export type AbsencePersonSummary = z.infer<typeof AbsencePersonSummary>;

export const AbsencesSummary = z.object({
  totalDays: z.number().int(),
  people: z.array(AbsencePersonSummary),
});
export type AbsencesSummary = z.infer<typeof AbsencesSummary>;

export const AbsenceBulkRulingRequest = z.object({
  ruling: AbsenceRuling,
  days: z
    .array(
      z.object({
        personId: z.string().min(1),
        businessDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      }),
    )
    .min(1)
    .max(200),
  note: z.string().max(500).optional(),
});
export type AbsenceBulkRulingRequest = z.infer<typeof AbsenceBulkRulingRequest>;

export const AbsenceBulkRulingResult = z.object({
  done: z.number().int(),
  failed: z.array(
    z.object({ personId: z.string(), businessDate: z.string(), message: z.string() }),
  ),
});
export type AbsenceBulkRulingResult = z.infer<typeof AbsenceBulkRulingResult>;

export const BackfillSessionRequest = z.object({
  clockInAt: z.string().min(1),
  clockOutAt: z.string().min(1),
  splits: z.array(
    z.object({
      taskId: z.string().min(1).nullable(),
      minutes: z.number().int().min(0),
      outcome: TaskWorkOutcome.nullable().default(null),
    }),
  ),
});
export type BackfillSessionRequest = z.infer<typeof BackfillSessionRequest>;

export const AttendanceDayRow = z.object({
  personId: z.string(),
  personName: z.string().nullable(),
  businessDate: z.string(),
  status: z.enum(['present', 'absent', 'excused', 'lop']),
  workedMinutes: z.number().int(),
  note: z.string().nullable(),
  backfillGranted: z.boolean(),
  resolvedBy: z.string().nullable(),
});
export type AttendanceDayRow = z.infer<typeof AttendanceDayRow>;

export const WorkSplitInput = z.object({
  taskId: z.string().min(1).nullable(),
  minutes: z.number().int().min(0),
  outcome: TaskWorkOutcome.nullable().default(null),
});
export type WorkSplitInput = z.infer<typeof WorkSplitInput>;

export const ClockOutRequest = z.object({
  splits: z.array(WorkSplitInput).min(1),
});
export type ClockOutRequest = z.infer<typeof ClockOutRequest>;

export const WorkSessionBreakSummary = z.object({
  id: z.string(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
});
export type WorkSessionBreakSummary = z.infer<typeof WorkSessionBreakSummary>;

export const WorkSessionSplitSummary = z.object({
  id: z.string(),
  taskId: z.string().nullable(),
  taskTitle: z.string().nullable(),
  projectId: z.string().nullable(),
  projectName: z.string().nullable(),
  declaredMinutes: z.number().int(),
  outcome: TaskWorkOutcome.nullable(),
  selectedAtClockIn: z.boolean(),
});
export type WorkSessionSplitSummary = z.infer<typeof WorkSessionSplitSummary>;

export const WorkSessionDetail = z.object({
  id: z.string(),
  personId: z.string(),
  personName: z.string().nullable(),
  businessDate: z.string(),
  clockInAt: z.string(),
  clockOutAt: z.string().nullable(),
  status: WorkSessionStatus,
  adjustsSessionId: z.string().nullable(),
  adjustmentReason: z.string().nullable(),
  workedMinutes: z.number().int(),
  breakMinutes: z.number().int(),
  onBreak: z.boolean(),
  breaks: z.array(WorkSessionBreakSummary),
  splits: z.array(WorkSessionSplitSummary),
  unmappedMinutes: z.number().int(),
  mappingDeadline: z.string(),
  mappingWindowOpen: z.boolean(),
  supersededById: z.string().nullable(),
});
export type WorkSessionDetail = z.infer<typeof WorkSessionDetail>;

export const CurrentWorkSessionResponse = WorkSessionDetail.nullable();
export type CurrentWorkSessionResponse = z.infer<typeof CurrentWorkSessionResponse>;

export const WorkSessionListFilters = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  personId: z.string().min(1).optional(),
});
export type WorkSessionListFilters = z.infer<typeof WorkSessionListFilters>;

export const WorkSessionAdjustRequest = z.object({
  reason: z.string().min(1).max(500),
  clockInAt: z.string().optional(),
  clockOutAt: z.string().optional(),
  breaks: z.array(z.object({ startedAt: z.string(), endedAt: z.string() })).optional(),
  splits: z.array(WorkSplitInput).min(1),
});
export type WorkSessionAdjustRequest = z.infer<typeof WorkSessionAdjustRequest>;

export const AllocationProposalRow = z.object({
  personId: z.string(),
  personName: z.string().nullable(),
  projectId: z.string(),
  projectName: z.string().nullable(),
  declaredMinutes: z.number().int(),
  percent: z.number().int(),
});
export type AllocationProposalRow = z.infer<typeof AllocationProposalRow>;

export const AllocationProposalResponse = z.object({
  month: z.string(),
  rows: z.array(AllocationProposalRow),
  unmappedMinutes: z.number().int(),
  confirmable: z.boolean(),
  blockedReason: z.string().nullable(),
});
export type AllocationProposalResponse = z.infer<typeof AllocationProposalResponse>;

export const AllocationProposalConfirmResponse = z.object({
  month: z.string(),
  written: z.number().int(),
});
export type AllocationProposalConfirmResponse = z.infer<typeof AllocationProposalConfirmResponse>;

// ============================================================
// Chat & calls (M14) — conversations, messages, directory
// ============================================================

export const ConversationKind = z.enum(['direct', 'group']);
export type ConversationKind = z.infer<typeof ConversationKind>;

export const ConversationStatus = z.enum(['active', 'archived']);
export type ConversationStatus = z.infer<typeof ConversationStatus>;

export const ConversationMemberRole = z.enum(['owner', 'member']);
export type ConversationMemberRole = z.infer<typeof ConversationMemberRole>;

export const ChatMessageKind = z.enum(['text', 'system', 'file', 'voice']);
export type ChatMessageKind = z.infer<typeof ChatMessageKind>;

// Message ids are BIGINT on the wire as decimal strings; the same string is the
// pagination cursor, the read position and the live-replay cursor.
export const ChatMessageId = z.string().regex(/^\d{1,19}$/);

export const ChatDirectoryEntry = z.object({
  id: z.string(),
  name: z.string(),
  employeeId: z.string().nullable(),
  email: z.string(),
  roles: z.array(RoleName),
  online: z.boolean(),
});
export type ChatDirectoryEntry = z.infer<typeof ChatDirectoryEntry>;

export const ChatDirectoryResponse = z.array(ChatDirectoryEntry);
export type ChatDirectoryResponse = z.infer<typeof ChatDirectoryResponse>;

export const ChatMemberSummary = z.object({
  personId: z.string(),
  name: z.string(),
  role: ConversationMemberRole,
  joinedAt: z.string(),
  lastReadMessageId: ChatMessageId.nullable(),
});
export type ChatMemberSummary = z.infer<typeof ChatMemberSummary>;

export const ChatReplyExcerpt = z.object({
  id: ChatMessageId,
  authorId: z.string().nullable(),
  authorName: z.string().nullable(),
  excerpt: z.string(),
  redacted: z.boolean(),
});
export type ChatReplyExcerpt = z.infer<typeof ChatReplyExcerpt>;

// `url` is the membership-checked download route for this message; images and
// voice audio come back inline, everything else as a download.
export const ChatAttachment = z.object({
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  durationMs: z.number().int().positive().nullable(),
  url: z.string(),
});
export type ChatAttachment = z.infer<typeof ChatAttachment>;

export const ChatReactionEmoji = z.enum(['👍', '❤️', '😂', '😮', '😢', '🙏']);
export type ChatReactionEmoji = z.infer<typeof ChatReactionEmoji>;

// Aggregated server-side in palette order; whether `personIds` includes the
// viewer is for the client to read, since one message fans out to everyone.
export const ChatReactionSummary = z.object({
  emoji: ChatReactionEmoji,
  count: z.number().int().positive(),
  personIds: z.array(z.string()),
});
export type ChatReactionSummary = z.infer<typeof ChatReactionSummary>;

export const ChatMessage = z.object({
  id: ChatMessageId,
  conversationId: z.string(),
  authorId: z.string().nullable(),
  authorName: z.string().nullable(),
  kind: ChatMessageKind,
  body: z.string(),
  meta: z.record(z.unknown()).nullable(),
  mentions: z.array(z.string()),
  replyTo: ChatReplyExcerpt.nullable(),
  attachment: ChatAttachment.nullable(),
  reactions: z.array(ChatReactionSummary),
  editedAt: z.string().nullable(),
  redacted: z.boolean(),
  createdAt: z.string(),
});
export type ChatMessage = z.infer<typeof ChatMessage>;

// Editing keeps every earlier body as a revision; a file caption may be
// edited down to nothing, a text body may not.
export const ChatMessageEditRequest = z.object({
  body: z.string().max(4000),
  mentions: z.array(z.string().min(1)).max(50).optional(),
});
export type ChatMessageEditRequest = z.infer<typeof ChatMessageEditRequest>;

export const ChatMessageRevision = z.object({
  id: z.string(),
  body: z.string(),
  supersededAt: z.string(),
  editedBy: z.string().nullable(),
  editedByName: z.string().nullable(),
});
export type ChatMessageRevision = z.infer<typeof ChatMessageRevision>;

export const ChatMessageRevisionsResponse = z.array(ChatMessageRevision);
export type ChatMessageRevisionsResponse = z.infer<typeof ChatMessageRevisionsResponse>;

export const ChatReactionRequest = z.object({
  emoji: ChatReactionEmoji,
});
export type ChatReactionRequest = z.infer<typeof ChatReactionRequest>;

export const ChatConversationSummary = z.object({
  id: z.string(),
  kind: ConversationKind,
  title: z.string().nullable(),
  status: ConversationStatus,
  members: z.array(ChatMemberSummary),
  lastMessage: ChatMessage.nullable(),
  unreadCount: z.number().int().nonnegative(),
  myRole: ConversationMemberRole,
  myLastReadMessageId: ChatMessageId.nullable(),
  createdAt: z.string(),
  activeCall: z.lazy(() => ChatCall).nullable(),
});
export type ChatConversationSummary = z.infer<typeof ChatConversationSummary>;

export const ChatConversationListResponse = z.array(ChatConversationSummary);
export type ChatConversationListResponse = z.infer<typeof ChatConversationListResponse>;

export const ChatDirectConversationRequest = z.object({
  personId: z.string().min(1),
});
export type ChatDirectConversationRequest = z.infer<typeof ChatDirectConversationRequest>;

export const ChatDirectConversationResponse = z.object({
  id: z.string(),
  created: z.boolean(),
});
export type ChatDirectConversationResponse = z.infer<typeof ChatDirectConversationResponse>;

export const ChatGroupCreateRequest = z.object({
  title: z.string().min(1).max(120),
  memberIds: z.array(z.string().min(1)).min(1).max(200),
});
export type ChatGroupCreateRequest = z.infer<typeof ChatGroupCreateRequest>;

export const ChatGroupUpdateRequest = z.object({
  title: z.string().min(1).max(120),
});
export type ChatGroupUpdateRequest = z.infer<typeof ChatGroupUpdateRequest>;

export const ChatMembersAddRequest = z.object({
  personIds: z.array(z.string().min(1)).min(1).max(200),
});
export type ChatMembersAddRequest = z.infer<typeof ChatMembersAddRequest>;

export const ChatMessageCreateRequest = z.object({
  body: z.string().min(1).max(4000),
  clientMessageId: z.string().regex(/^[0-9A-Za-z]{10,26}$/),
  replyToId: ChatMessageId.optional(),
  mentions: z.array(z.string().min(1)).max(50).optional(),
});
export type ChatMessageCreateRequest = z.infer<typeof ChatMessageCreateRequest>;

// The `metadata` JSON part of a multipart upload to
// `POST /chat/conversations/:id/attachments`; the `file` part is the bytes.
export const ChatAttachmentKind = z.enum(['file', 'voice']);
export type ChatAttachmentKind = z.infer<typeof ChatAttachmentKind>;

export const ChatAttachmentUploadMetadata = z.object({
  kind: ChatAttachmentKind,
  clientMessageId: z.string().regex(/^[0-9A-Za-z]{10,26}$/),
  caption: z.string().max(4000).optional(),
  replyToId: ChatMessageId.optional(),
  mentions: z.array(z.string().min(1)).max(50).optional(),
  durationMs: z.number().int().positive().optional(),
});
export type ChatAttachmentUploadMetadata = z.infer<typeof ChatAttachmentUploadMetadata>;

export const ChatMessagesQuery = z.object({
  before: ChatMessageId.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type ChatMessagesQuery = z.infer<typeof ChatMessagesQuery>;

export const ChatMessagesResponse = z.object({
  messages: z.array(ChatMessage),
  hasMore: z.boolean(),
});
export type ChatMessagesResponse = z.infer<typeof ChatMessagesResponse>;

export const ChatReadRequest = z.object({
  upToMessageId: ChatMessageId,
});
export type ChatReadRequest = z.infer<typeof ChatReadRequest>;

export const ChatSyncQuery = z.object({
  after: ChatMessageId,
});
export type ChatSyncQuery = z.infer<typeof ChatSyncQuery>;

export const ChatSyncResponse = z.object({
  messages: z.array(ChatMessage),
  cursor: ChatMessageId,
  truncated: z.boolean(),
});
export type ChatSyncResponse = z.infer<typeof ChatSyncResponse>;

export const ChatSearchQuery = z.object({
  q: z.string().trim().min(2).max(200),
});
export type ChatSearchQuery = z.infer<typeof ChatSearchQuery>;

export const ChatSearchResponse = z.array(ChatMessage);
export type ChatSearchResponse = z.infer<typeof ChatSearchResponse>;

// ---- Live stream (`GET /chat/stream`, text/event-stream) ----
// Only `message` events carry an SSE `id:` (the message id), so the browser's
// Last-Event-ID on reconnect is always a message cursor. `after` is the same
// cursor supplied explicitly on first connect.

export const ChatStreamQuery = z.object({
  after: ChatMessageId.optional(),
});
export type ChatStreamQuery = z.infer<typeof ChatStreamQuery>;

export const ChatStreamEventName = z.enum([
  'ready',
  'message',
  'message.redacted',
  'message.updated',
  'conversation',
  'read',
  'typing',
  'presence',
  'call.incoming',
  'call.updated',
  'call.answered_elsewhere',
  'call.signal',
]);
export type ChatStreamEventName = z.infer<typeof ChatStreamEventName>;

export const ChatStreamReadyEvent = z.object({
  personId: z.string(),
  online: z.array(z.string()),
  serverTime: z.string(),
});
export type ChatStreamReadyEvent = z.infer<typeof ChatStreamReadyEvent>;

export const ChatStreamMessageEvent = z.object({
  message: ChatMessage,
});
export type ChatStreamMessageEvent = z.infer<typeof ChatStreamMessageEvent>;

export const ChatStreamMessageRedactedEvent = z.object({
  conversationId: z.string(),
  messageId: ChatMessageId,
});
export type ChatStreamMessageRedactedEvent = z.infer<typeof ChatStreamMessageRedactedEvent>;

// An edit or a reaction change: the whole message again, not a delta, so a
// client that missed earlier events still ends up with the current state.
// Carries no `id:` and is not replayed; reconnects refetch open threads.
export const ChatStreamMessageUpdatedEvent = z.object({
  message: ChatMessage,
});
export type ChatStreamMessageUpdatedEvent = z.infer<typeof ChatStreamMessageUpdatedEvent>;

// `conversation: null` tells a recipient they are no longer a member.
export const ChatStreamConversationEvent = z.object({
  conversationId: z.string(),
  conversation: ChatConversationSummary.nullable(),
});
export type ChatStreamConversationEvent = z.infer<typeof ChatStreamConversationEvent>;

export const ChatStreamReadEvent = z.object({
  conversationId: z.string(),
  personId: z.string(),
  upToMessageId: ChatMessageId,
});
export type ChatStreamReadEvent = z.infer<typeof ChatStreamReadEvent>;

export const ChatStreamTypingEvent = z.object({
  conversationId: z.string(),
  personId: z.string(),
});
export type ChatStreamTypingEvent = z.infer<typeof ChatStreamTypingEvent>;

export const ChatStreamPresenceEvent = z.object({
  personId: z.string(),
  online: z.boolean(),
});
export type ChatStreamPresenceEvent = z.infer<typeof ChatStreamPresenceEvent>;

// ---- Calls ----

export const CallKind = z.enum(['audio', 'video']);
export type CallKind = z.infer<typeof CallKind>;

export const CallStatus = z.enum(['ringing', 'active', 'ended']);
export type CallStatus = z.infer<typeof CallStatus>;

export const CallEndReason = z.enum(['completed', 'missed', 'declined', 'cancelled', 'failed']);
export type CallEndReason = z.infer<typeof CallEndReason>;

export const CallParticipantStatus = z.enum(['invited', 'joined', 'left', 'declined', 'missed']);
export type CallParticipantStatus = z.infer<typeof CallParticipantStatus>;

// Fixed when the call starts: every participant uses the same one.
export const CallTransport = z.enum(['mesh', 'sfu']);
export type CallTransport = z.infer<typeof CallTransport>;

export const ChatCallTrackName = z.enum(['mic', 'cam', 'screen']);
export type ChatCallTrackName = z.infer<typeof ChatCallTrackName>;

export const ChatCallTrack = z.object({
  name: ChatCallTrackName,
  kind: z.enum(['audio', 'video']),
});
export type ChatCallTrack = z.infer<typeof ChatCallTrack>;

// What a joined participant publishes on an SFU call; others subscribe to
// (sessionId, name) pairs. Null on mesh calls and once they have left.
export const ChatCallSession = z.object({
  sessionId: z.string(),
  tracks: z.array(ChatCallTrack),
});
export type ChatCallSession = z.infer<typeof ChatCallSession>;

export const ChatCallParticipant = z.object({
  personId: z.string(),
  name: z.string(),
  status: CallParticipantStatus,
  joinedAt: z.string().nullable(),
  leftAt: z.string().nullable(),
  session: ChatCallSession.nullable(),
});
export type ChatCallParticipant = z.infer<typeof ChatCallParticipant>;

export const ChatCall = z.object({
  id: z.string(),
  conversationId: z.string(),
  kind: CallKind,
  transport: CallTransport,
  status: CallStatus,
  endReason: CallEndReason.nullable(),
  startedBy: z.string(),
  startedAt: z.string(),
  answeredAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  participants: z.array(ChatCallParticipant),
});
export type ChatCall = z.infer<typeof ChatCall>;

export const ChatCallStartRequest = z.object({
  conversationId: z.string().min(1),
  kind: CallKind,
});
export type ChatCallStartRequest = z.infer<typeof ChatCallStartRequest>;

// `state` is a tiny per-call note between two participants ("I am sharing my
// screen"); like the others it is relayed verbatim and never parsed here.
export const ChatSignalType = z.enum(['offer', 'answer', 'ice', 'state']);
export type ChatSignalType = z.infer<typeof ChatSignalType>;

// Relayed verbatim between two participants; the server checks shape and size
// and never parses SDP.
export const CHAT_SIGNAL_MAX_BYTES = 64 * 1024;
export const ChatCallSignalRequest = z.object({
  to: z.string().min(1),
  type: ChatSignalType,
  payload: z
    .unknown()
    .refine((p) => JSON.stringify(p ?? null).length <= CHAT_SIGNAL_MAX_BYTES, 'Signal too large'),
});
export type ChatCallSignalRequest = z.infer<typeof ChatCallSignalRequest>;

// ---- Calls over the SFU (transport 'sfu') ----
// The browser never talks to the SFU: these are the call-scoped proxies.
// Session descriptions are passed through; the server never parses SDP.

export const ChatSessionDescription = z.object({
  type: z.enum(['offer', 'answer']),
  sdp: z.string().min(1).max(CHAT_SIGNAL_MAX_BYTES),
});
export type ChatSessionDescription = z.infer<typeof ChatSessionDescription>;

export const ChatSfuSessionResponse = z.object({ sessionId: z.string() });
export type ChatSfuSessionResponse = z.infer<typeof ChatSfuSessionResponse>;

export const ChatSfuPublishRequest = z.object({
  sessionDescription: ChatSessionDescription,
  tracks: z
    .array(z.object({ name: ChatCallTrackName, kind: z.enum(['audio', 'video']), mid: z.string().min(1) }))
    .min(1)
    .max(3),
});
export type ChatSfuPublishRequest = z.infer<typeof ChatSfuPublishRequest>;

export const ChatSfuPublishResponse = z.object({ sessionDescription: ChatSessionDescription });
export type ChatSfuPublishResponse = z.infer<typeof ChatSfuPublishResponse>;

export const ChatSfuSubscribeRequest = z.object({
  tracks: z.array(z.object({ sessionId: z.string().min(1), name: ChatCallTrackName })).min(1).max(64),
});
export type ChatSfuSubscribeRequest = z.infer<typeof ChatSfuSubscribeRequest>;

// `sessionDescription` is the SFU's offer to answer through renegotiate;
// null when the SFU had nothing to renegotiate.
export const ChatSfuSubscribeResponse = z.object({
  sessionDescription: ChatSessionDescription.nullable(),
  tracks: z.array(z.object({ sessionId: z.string(), name: ChatCallTrackName, mid: z.string() })),
});
export type ChatSfuSubscribeResponse = z.infer<typeof ChatSfuSubscribeResponse>;

export const ChatSfuRenegotiateRequest = z.object({ sessionDescription: ChatSessionDescription });
export type ChatSfuRenegotiateRequest = z.infer<typeof ChatSfuRenegotiateRequest>;

export const ChatSfuUnpublishRequest = z.object({
  tracks: z.array(z.object({ name: ChatCallTrackName })).min(1).max(3),
});
export type ChatSfuUnpublishRequest = z.infer<typeof ChatSfuUnpublishRequest>;

export const ChatSfuUnsubscribeRequest = z.object({
  tracks: z.array(z.object({ mid: z.string().min(1) })).min(1).max(64),
});
export type ChatSfuUnsubscribeRequest = z.infer<typeof ChatSfuUnsubscribeRequest>;

export const ChatIceServer = z.object({

  urls: z.array(z.string()).min(1),
  username: z.string().optional(),
  credential: z.string().optional(),
});
export type ChatIceServer = z.infer<typeof ChatIceServer>;

// `relay: false` means STUN only: calls between two strict NATs will not
// connect, and the UI says so.
export const ChatIceServersResponse = z.object({
  iceServers: z.array(ChatIceServer),
  relay: z.boolean(),
  expiresAt: z.string().nullable(),
});
export type ChatIceServersResponse = z.infer<typeof ChatIceServersResponse>;

export const ChatStreamCallIncomingEvent = z.object({
  call: ChatCall,
  from: z.object({ personId: z.string(), name: z.string() }),
});
export type ChatStreamCallIncomingEvent = z.infer<typeof ChatStreamCallIncomingEvent>;

export const ChatStreamCallUpdatedEvent = z.object({
  call: ChatCall,
});
export type ChatStreamCallUpdatedEvent = z.infer<typeof ChatStreamCallUpdatedEvent>;

export const ChatStreamCallAnsweredElsewhereEvent = z.object({
  callId: z.string(),
});
export type ChatStreamCallAnsweredElsewhereEvent = z.infer<
  typeof ChatStreamCallAnsweredElsewhereEvent
>;

export const ChatStreamCallSignalEvent = z.object({
  callId: z.string(),
  from: z.string(),
  type: ChatSignalType,
  payload: z.unknown(),
});
export type ChatStreamCallSignalEvent = z.infer<typeof ChatStreamCallSignalEvent>;
